<template>
  <div class="document-container">
    <div class="columns">
      <div class="column">
        <div class="document" style="height: 100%">
          <div class="header">
            <es-header>
              <template #header>
                <div class="header-title">
                  Add a New Person
                </div>
              </template>
            </es-header>
          </div>
          <new-match-message
            v-if="dataSubmitted"
            :addNewPerson="true"
            @newMatch="resetData()"
            style="padding: 1rem 2rem"
          >
            <template v-slot:message>
              Batch submitted. Review details below.
            </template>
            <template v-slot:button-text>
              Add a New Person
            </template>
          </new-match-message>
          <es-loader
            v-else-if="loading"
            style="margin: 6.5rem"
          />
          <es-exception
            v-else-if="loadingError"
            style="padding-bottom: 7rem;"
            exceptionMessage="There was an issue.
            Please try again or contact your administrator."
          />
          <div v-else>
            <es-form :padding="{ padding: '2.5rem 0rem 0rem 0rem' }">
              <template #es-form-header>
                <div class="header-container">
                  <div class="header-subtitle">
                    When the Add a New Person page loads,
                    the default person type is automatically selected.
                    Each person type may have different field configurations,
                    so ensure the correct person type is selected before filling out the form.
                  </div>
                </div>
              </template>
              <template #es-form>
                <div class="row removeUnderline">
                  <div class="field is-horizontal" style="float: left">
                    <div class="field-label is-normal" style="max-width: 100px;">
                      <label class="label">
                        Person Type
                      </label>
                    </div>
                    <div class="field-body">
                      <div class="buttons has-addons">
                        <button
                          v-for="(item, index) in sortedPersonTypesOptions"
                          :key="index"
                          class="button button--form"
                          @click="personType = item.code; personTypeFieldReset()"
                          :class="{'is-active': personType === item.code}"
                          :disabled="!enabledSubmissionStatus.includes(item.code)"
                          style="min-width: 100px"
                        >
                          {{ item.name }}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
                <div v-if="hasModule('PRSID:MATCH:SUBMPURP:007MATCH') && !contextMode">
                  <div class="row removeUnderline" >
                    <div class="field is-horizontal" style="float: left">
                      <div class="field-label is-normal" style="max-width: 100px;">
                        <label class="label">
                          Submission Purpose
                        </label>
                      </div>
                      <div class="field-body">
                        <div class="buttons has-addons">
                          <button
                            v-for="(item, code) in submissionPurposes"
                            :key="code"
                            class="button button--form"
                            @click="submissionPurpose = item.code;"
                            :class="{'is-active': submissionPurpose === item.code}"
                            style="min-width: 100px"
                          >
                            {{ item.description }}
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </template>
            </es-form>
            <div class="content" style="padding: 0 0 1rem">
              <div class="columns is-multiline is-marginless is-multicolumns">
                <div class="column master-record--column">
                  <es-form :margin="{ margin: '0' }">
                    <template #es-form-header style="margin: 0">
                      <div class="header-container">
                        <div class="header-elements">
                          <font-awesome-icon :icon="['fas', 'user']" size="lg"/>
                        </div>
                        <div class="header-elements">
                          <h6 class="title">
                            Person Information
                          </h6>
                        </div>
                      </div>
                    </template>
                    <template #es-form>
                      <div class="row removeUnderline">
                        <div class="field is-horizontal">
                          <div class="field-label is-normal">
                            <label class="label">
                              {{ fieldLabel('FIRST_NAME') }}
                            </label>
                          </div>
                          <div class="field-body">
                            <div class="field">
                              <div class="control">
                                <input
                                  type="text"
                                  class="input"
                                  :class="{'is-danger' : hasErrorFirstName}"
                                  v-model="firstName"
                                  @keyup.prevent="validateInputField('firstName')"
                                  @paste="onPasteValidation('firstName')"
                                  :aria-label="fieldLabel('FIRST_NAME')"
                                >
                              </div>
                              <span v-if="hasErrorFirstName" class="help is-danger">
                                {{ msgFirstName }}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div class="row removeUnderline" v-if= "fieldEnabled('MIDDLE_NAME')">
                        <div class="field is-horizontal">
                          <div class="field-label is-normal">
                            <label class="label">
                              {{ fieldLabel('MIDDLE_NAME') }}
                              <span class="label-optional has-text-grey-light"
                                v-if= "!fieldRequired('MIDDLE_NAME')">
                                (optional)
                              </span>
                            </label>
                          </div>
                          <div class="field-body">
                            <div class="field">
                              <div class="control">
                                <input
                                  class="input"
                                  type="text"
                                  :class="{'is-danger' : hasErrorMiddleName}"
                                  v-model="middleName"
                                  @keyup.prevent="validateInputField('middleName')"
                                  @paste="onPasteValidation('middleName')"
                                  :aria-label="fieldLabel('MIDDLE_NAME')"
                                >
                              </div>
                              <span v-if="hasErrorMiddleName" class="help is-danger">
                                {{ msgMiddleName }}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div class="row removeUnderline">
                        <div class="field is-horizontal">
                          <div class="field-label is-normal">
                            <label class="label">
                              {{ fieldLabel('LAST_NAME') }}
                            </label>
                          </div>
                          <div class="field-body">
                            <div class="field">
                              <div class="control">
                                <input
                                  class="input"
                                  type="text"
                                  :class="{'is-danger' : hasErrorLastName}"
                                  v-model="lastName"
                                  @keyup.prevent="validateInputField('lastName')"
                                  @paste="onPasteValidation('lastName')"
                                  :aria-label="fieldLabel('LAST_NAME')"
                                >
                              </div>
                              <span v-if="hasErrorLastName" class="help is-danger">
                                {{ msgLastName }}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div class="row removeUnderline" v-if= "fieldEnabled('SUFFIX')">
                        <div class="field is-horizontal">
                          <div class="field-label is-normal">
                            <label class="label">
                              {{ fieldLabel('SUFFIX') }}
                              <span class="label-optional has-text-grey-light"
                                v-if= "!fieldRequired('SUFFIX')">
                                (optional)
                              </span>
                            </label>
                          </div>
                          <div class="field-body">
                            <div class="field">
                              <div class="control">
                                <input
                                  class="input"
                                  type="text"
                                  :class="{'is-danger' : hasErrorSuffix}"
                                  v-model="suffix"
                                  @keyup.prevent="validateInputField('suffix')"
                                  @paste="onPasteValidation('suffix')"
                                  :aria-label="fieldLabel('SUFFIX')"
                                >
                              </div>
                              <span v-if="hasErrorSuffix" class="help is-danger">
                                {{ msgSuffix }}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div class="row removeUnderline" v-if= "fieldEnabled('PREVIOUS_LAST_NAME')">
                        <div class="field is-horizontal">
                          <div class="field-label is-normal">
                            <label class="label">
                              {{ fieldLabel('PREVIOUS_LAST_NAME') }}
                              <span class="label-optional has-text-grey-light"
                                v-if= "!fieldRequired('PREVIOUS_LAST_NAME')">
                                (optional)
                              </span>
                            </label>
                          </div>
                          <div class="field-body">
                            <div class="field">
                              <div class="control">
                                <input
                                  class="input"
                                  type="text"
                                  :class="{'is-danger' : hasErrorAltLastName}"
                                  v-model="altLastName"
                                  @keyup.prevent="validateInputField('altLastName')"
                                  @paste="onPasteValidation('altLastName')"
                                  :aria-label="fieldLabel('PREVIOUS_LAST_NAME')"
                                >
                              </div>
                              <span v-if="hasErrorAltLastName" class="help is-danger">
                                {{ msgAltLastName }}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div class="row removeUnderline" v-if= "fieldEnabled('FULL_NAME')">
                        <div class="field is-horizontal">
                          <div class="field-label is-normal">
                            <label class="label">
                              {{ fieldLabel('FULL_NAME') }}
                              <span class="label-optional has-text-grey-light"
                                v-if= "!fieldRequired('FULL_NAME')">
                                (optional)
                              </span>
                            </label>
                          </div>
                          <div class="field-body">
                            <div class="field">
                              <div class="control">
                                <input
                                  class="input"
                                  type="text"
                                  :class="{'is-danger' : hasErrorFullName}"
                                  v-model="fullName"
                                  @keyup.prevent="validateInputField('fullName')"
                                  @paste="onPasteValidation('fullName')"
                                  :aria-label="fieldLabel('FULL_NAME')"
                                >
                              </div>
                              <span v-if="hasErrorFullName" class="help is-danger">
                                {{ msgFullName }}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div class="row removeUnderline" v-if= "fieldEnabled('AKA_NAME')">
                        <div class="field is-horizontal">
                          <div class="field-label is-normal">
                            <label class="label">
                              {{ fieldLabel('AKA_NAME') }}
                              <span class="label-optional has-text-grey-light"
                                v-if= "!fieldRequired('AKA_NAME')">
                                (optional)
                              </span>
                            </label>
                          </div>
                          <div class="field-body">
                            <div class="field">
                              <div class="control">
                                <input
                                  class="input"
                                  type="text"
                                  :class="{'is-danger' : hasErrorNickName}"
                                  v-model="nickName"
                                  @keyup.prevent="validateInputField('nickName')"
                                  @paste="onPasteValidation('nickName')"
                                  :aria-label="fieldLabel('AKA_NAME')"
                                >
                              </div>
                              <span v-if="hasErrorNickName" class="help is-danger">
                                {{ msgNickName }}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div class="row removeUnderline">
                        <div class="field is-horizontal">
                          <div class="field-label is-normal">
                            <label class="label">
                              {{ fieldLabel('GENDER_CODE') }}
                            </label>
                          </div>
                          <div class="field-body">
                            <div class="field">
                              <div class="control">
                                <div class="buttons has-addons">
                                  <button
                                    v-for="(item, index) in activeGenders"
                                    :key="index"
                                    class="button button--form"
                                    @click="gender = item.code; validateField('gender')"
                                    :class="{
                                      'is-active': gender === item.code,
                                      'gender-empty': hasErrorGender
                                      }"
                                      style="min-width: 100px"
                                  >
                                    {{ item.description }}
                                  </button>
                                </div>
                              </div>
                              <span v-if ="hasErrorGender" class="help is-danger">
                                {{ msgGender }}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div class="row removeUnderline">
                        <div class="field is-horizontal">
                          <div class="field-label is-normal">
                            <label class="label">
                              {{ fieldLabel('DATE_OF_BIRTH') }}
                            </label>
                          </div>
                          <div class="field-body">
                            <div class="field">
                              <div class="control">
                                <input
                                  class="input calendar"
                                  :class="{
                                    'placeholder': !dateOfBirth,
                                    'is-danger' : hasErrorDateOfBirth
                                  }"
                                  type="date"
                                  v-model="dateOfBirth"
                                  @change.prevent="validateField('dateOfBirth')"
                                  :aria-label="fieldLabel('DATE_OF_BIRTH')"
                                />
                              </div>
                              <span v-if="hasErrorDateOfBirth" class="help is-danger">
                                {{ msgDateOfBirth }}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div
                        v-if= "fieldEnabled('SOCIAL_SECURITY_NUMBER')"
                        class="row removeUnderline"
                      >
                        <div class="field is-horizontal">
                          <div class="field-label is-normal">
                            <label class="label">
                              {{ fieldLabel('SOCIAL_SECURITY_NUMBER') }}
                              <span class="label-optional has-text-grey-light"
                                v-if= "!fieldRequired('SOCIAL_SECURITY_NUMBER')">
                                (optional)
                              </span>
                            </label>
                          </div>
                          <div class="field-body">
                            <div class="field">
                              <div class="control">
                                <input class="input"
                                  placeholder="###-##-#### OR #########"
                                  type="text"
                                  :class="{'is-danger' : hasErrorSSN}"
                                  @keypress="isSSN($event)"
                                  @keyup.prevent="validateField('ssn')"
                                  v-model="ssn"
                                  :aria-label="fieldLabel('SOCIAL_SECURITY_NUMBER')"
                                >
                              </div>
                              <span v-if="hasErrorSSN" class="help is-danger">
                                {{ msgSSN }}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div class="row removeUnderline" v-if= "fieldEnabled('ETHNICITY_INDICATOR')">
                        <div class="field is-horizontal">
                          <div class="field-label is-normal">
                            <label class="label">
                              {{ fieldLabel('ETHNICITY_INDICATOR') }}
                              <span class="label-optional has-text-grey-light"
                                v-if= "!fieldRequired('ETHNICITY_INDICATOR')">
                                (optional)
                              </span>
                            </label>
                          </div>
                          <div class="field-body">
                            <div class="field">
                              <div class="control">
                                <es-select
                                  valueTpl="${description}"
                                  :setValues="activeEthnicities"
                                  keyValue="code"
                                  v-on:update:values="ethnicity = $event;
                                    validateSelectField('ethnicity')"
                                  v-bind:clearOut="true"
                                  ref="resetEthnicity"
                                  :hasError="hasErrorEthnicity"
                                  :errorMsg="msgEthnicity"
                                />
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div class="row removeUnderline" v-if= "fieldEnabled('RACE_ETHNIC_CODE')">
                        <div class="field is-horizontal">
                          <div class="field-label is-normal">
                            <label class="label">
                              {{ fieldLabel('RACE_ETHNIC_CODE') }}
                              <span class="label-optional has-text-grey-light"
                                v-if= "!fieldRequired('RACE_ETHNIC_CODE')">
                                (optional)
                              </span>
                            </label>
                          </div>
                          <div class="field-body">
                            <div class="field">
                              <div class="control">
                                <es-select
                                  valueTpl="${description}"
                                  :setValues="availableRaces"
                                  keyValue="code"
                                  v-on:update:values="race1 = $event;
                                    validateSelectField('race1')"
                                  v-bind:clearOut="true"
                                  ref="resetRace"
                                  :hasError="hasErrorRaceEthnicCode"
                                  :errorMsg="msgRaceEthnicCode"
                                />
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div class="row removeUnderline" v-if= "fieldEnabled('RACE2_CODE')">
                        <div class="field is-horizontal">
                          <div class="field-label is-normal">
                            <label class="label">
                              {{ fieldLabel('RACE2_CODE') }}
                              <span class="label-optional has-text-grey-light"
                                v-if= "!fieldRequired('RACE2_CODE')">
                                (optional)
                              </span>
                            </label>
                          </div>
                          <div class="field-body">
                            <div class="field">
                              <div class="control">
                                <es-select
                                  valueTpl="${description}"
                                  :setValues="availableRaces"
                                  keyValue="code"
                                  v-on:update:values="race2 = $event;
                                    validateSelectField('race2')"
                                  v-bind:clearOut="true"
                                  ref="resetRace2"
                                  :hasError="hasErrorRace2Code"
                                  :errorMsg="msgRace2Code"
                                />
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div class="row removeUnderline" v-if= "fieldEnabled('RACE3_CODE')">
                        <div class="field is-horizontal">
                          <div class="field-label is-normal">
                            <label class="label">
                              {{ fieldLabel('RACE3_CODE') }}
                              <span class="label-optional has-text-grey-light"
                                v-if= "!fieldRequired('RACE3_CODE')">
                                (optional)
                              </span>
                            </label>
                          </div>
                          <div class="field-body">
                            <div class="field">
                              <div class="control">
                                <es-select
                                  valueTpl="${description}"
                                  :setValues="availableRaces"
                                  keyValue="code"
                                  v-on:update:values="race3 = $event;
                                    validateSelectField('race3')"
                                  v-bind:clearOut="true"
                                  ref="resetRace3"
                                  :hasError="hasErrorRace3Code"
                                  :errorMsg="msgRace3Code"
                                />
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div class="row removeUnderline" v-if= "fieldEnabled('RACE4_CODE')">
                        <div class="field is-horizontal">
                          <div class="field-label is-normal">
                            <label class="label">
                              {{ fieldLabel('RACE4_CODE') }}
                              <span class="label-optional has-text-grey-light"
                                v-if= "!fieldRequired('RACE4_CODE')">
                                (optional)
                              </span>
                            </label>
                          </div>
                          <div class="field-body">
                            <div class="field">
                              <div class="control">
                                <es-select
                                  valueTpl="${description}"
                                  :setValues="availableRaces"
                                  keyValue="code"
                                  v-on:update:values="race4 = $event;
                                    validateSelectField('race4')"
                                  v-bind:clearOut="true"
                                  ref="resetRace4"
                                  :hasError="hasErrorRace4Code"
                                  :errorMsg="msgRace4Code"
                                />
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div class="row removeUnderline" v-if= "fieldEnabled('RACE5_CODE')">
                        <div class="field is-horizontal">
                          <div class="field-label is-normal">
                            <label class="label">
                              {{ fieldLabel('RACE5_CODE') }}
                              <span class="label-optional has-text-grey-light"
                                v-if= "!fieldRequired('RACE5_CODE')">
                                (optional)
                              </span>
                            </label>
                          </div>
                          <div class="field-body">
                            <div class="field">
                              <div class="control">
                                <es-select
                                  valueTpl="${description}"
                                  :setValues="availableRaces"
                                  keyValue="code"
                                  v-on:update:values="race5 = $event;
                                    validateSelectField('race5')"
                                  v-bind:clearOut="true"
                                  ref="resetRace5"
                                  :hasError="hasErrorRace5Code"
                                  :errorMsg="msgRace5Code"
                                />
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div class="row removeUnderline" v-if= "fieldEnabled('ETHNIC_SUB_GROUP')">
                        <div class="field is-horizontal">
                          <div class="field-label is-normal">
                            <label class="label">
                              {{ fieldLabel('ETHNIC_SUB_GROUP') }}
                              <span class="label-optional has-text-grey-light"
                                v-if= "!fieldRequired('ETHNIC_SUB_GROUP')">
                                (optional)
                              </span>
                            </label>
                          </div>
                          <div class="field-body">
                            <div class="field">
                              <div class="control">
                                <es-select
                                  valueTpl="${description}"
                                  :setValues="activeEthnicSubGroups"
                                  keyValue="code"
                                  v-on:update:values="ethnicSubGroup = $event;
                                    validateSelectField('ethnicSubGroup')"
                                  v-bind:clearOut="true"
                                  ref="resetEthnicSubgroup"
                                  :hasError="hasErrorEthnicSubGroup"
                                  :errorMsg="msgEthnicSubGroup"
                                />
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div class="row removeUnderline" v-if= "fieldEnabled('STATE_STUDENT_ID')">
                        <div class="field is-horizontal">
                          <div class="field-label is-normal">
                            <label class="label">
                              {{ fieldLabel('STATE_STUDENT_ID') }}
                              <span class="label-optional has-text-grey-light"
                                v-if="
                                  !fieldRequired('STATE_STUDENT_ID')
                                  && !submissionPurposeRequiresStateId"
                              >
                                (optional)
                              </span>
                            </label>
                          </div>
                          <div class="field-body">
                            <div class="field">
                              <div class="control">
                                <input
                                  class="input"
                                  type="text"
                                  :class="{'is-danger' : hasErrorStateId}"
                                  @keyup.prevent="validateInputField('stateId')"
                                  @paste="onPasteValidation('stateId', $event)"
                                  @keypress="isWholeNumbers($event)"
                                  v-model="stateId"
                                  :aria-label="fieldLabel('STATE_STUDENT_ID')"
                                  autocomplete="nope"
                                >
                                <span v-if ="hasErrorStateId" class="help is-danger">
                                  {{ msgStateId }}
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </template>
                  </es-form>
                </div>
                <div v-if="contactInfoFieldsEnabled()" class="column master-record--column">
                  <es-form :margin="{ margin: '0' }">
                    <template #es-form-header style="margin: 0">
                      <div class="header-container">
                        <div class="header-elements">
                          <font-awesome-icon :icon="['fas', 'address-book']" size="lg"/>
                        </div>
                        <div class="header-elements">
                          <h6 class="title">
                            Contact Information
                          </h6>
                        </div>
                      </div>
                    </template>
                    <template #es-form>
                      <div class="row removeUnderline" v-if= "fieldEnabled('STREET_1')">
                        <div class="field is-horizontal">
                          <div class="field-label is-normal">
                            <label class="label">
                              {{ fieldLabel('STREET_1') }}
                              <span class="label-optional has-text-grey-light"
                                v-if= "!fieldRequired('STREET_1')">
                                (optional)
                              </span>
                            </label>
                          </div>
                          <div class="field-body">
                            <div class="field">
                              <div class="control">
                                <input
                                  class="input"
                                  type="text"
                                  :class="{'is-danger' : hasErrorAddress1}"
                                  v-model="address1"
                                  @keyup.prevent="validateInputField('address1')"
                                  @paste="onPasteValidation('address1')"
                                  :aria-label="fieldLabel('STREET_1')"
                                >
                              </div>
                              <span v-if="hasErrorAddress1" class="help is-danger">
                                {{ msgAddress1 }}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div class="row removeUnderline" v-if= "fieldEnabled('STREET_2')">
                        <div class="field is-horizontal">
                          <div class="field-label is-normal">
                            <label class="label">
                               {{ fieldLabel('STREET_2') }}
                              <span class="label-optional has-text-grey-light"
                                v-if= "!fieldRequired('STREET_2')">
                                (optional)
                              </span>
                            </label>
                          </div>
                          <div class="field-body">
                            <div class="field">
                              <div class="control">
                                <input
                                  class="input"
                                  type="text"
                                  :class="{'is-danger' : hasErrorAddress2}"
                                  v-model="address2"
                                  @keyup.prevent="validateInputField('address2')"
                                  @paste="onPasteValidation('address2')"
                                  :aria-label="fieldLabel('STREET_2')"
                                >
                              </div>
                              <span v-if="hasErrorAddress2" class="help is-danger">
                                {{ msgAddress2 }}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div class="row removeUnderline" v-if= "fieldEnabled('CITY')">
                        <div class="field is-horizontal">
                          <div class="field-label is-normal">
                            <label class="label">
                                {{ fieldLabel('CITY') }}
                              <span class="label-optional has-text-grey-light"
                                v-if= "!fieldRequired('CITY')">
                                (optional)
                              </span>
                            </label>
                          </div>
                          <div class="field-body">
                            <div class="field">
                              <div class="control">
                                <input
                                  class="input"
                                  type="text"
                                  :class="{'is-danger' : hasErrorCity}"
                                  v-model="city"
                                  @keyup.prevent="validateInputField('city')"
                                  @paste="onPasteValidation('city')"
                                  :aria-label="fieldLabel('CITY')"
                                >
                              </div>
                              <span v-if="hasErrorCity" class="help is-danger">
                                {{ msgCity }}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div class="row removeUnderline" v-if= "fieldEnabled('STATE_CODE')">
                        <div class="field is-horizontal">
                          <div class="field-label is-normal">
                            <label class="label">
                              {{ fieldLabel('STATE_CODE') }}
                              <span class="label-optional has-text-grey-light"
                                v-if= "!fieldRequired('STATE_CODE')">
                                (optional)
                              </span>
                            </label>
                          </div>
                          <div class="field-body">
                            <div class="field">
                              <div class="control">
                                <es-select
                                  valueTpl="${description}"
                                  :setValues="activeStates"
                                  keyValue="code"
                                  v-on:update:values="state = $event;
                                   validateSelectField('state')"
                                  v-bind:clearOut="true"
                                  ref="resetState"
                                  :hasError="hasErrorState"
                                  :errorMsg="msgState"
                                />
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div class="row removeUnderline" v-if= "fieldEnabled('ZIP')">
                        <div class="field is-horizontal">
                          <div class="field-label is-normal">
                            <label class="label">
                              {{ fieldLabel('ZIP') }}
                              <span class="label-optional has-text-grey-light"
                                v-if= "!fieldRequired('ZIP')">
                                (optional)
                              </span>
                            </label>
                          </div>
                          <div class="field-body">
                            <div class="field">
                              <div class="control">
                                <input
                                  class="input"
                                  type="text"
                                  :class="{'is-danger' : hasErrorZip}"
                                  @keyup.prevent="validateField('zip')"
                                  @paste="onPasteValidation('zip', $event)"
                                  v-model="zip"
                                  :aria-label="fieldLabel('ZIP')"
                                >
                              </div>
                              <span v-if="hasErrorZip" class="help is-danger">
                                {{ msgZip }}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div class="row removeUnderline" v-if= "fieldEnabled('GEOCODE')">
                        <div class="field is-horizontal">
                          <div class="field-label is-normal">
                            <label class="label">
                                {{ fieldLabel('GEOCODE') }}
                              <span class="label-optional has-text-grey-light"
                                v-if= "!fieldRequired('GEOCODE')">
                                (optional)
                              </span>
                            </label>
                          </div>
                          <div class="field-body">
                            <div class="field">
                              <div class="control">
                                <input
                                  class="input"
                                  type="text"
                                  :class="{'is-danger' : hasErrorGeoCode}"
                                  @keyup.prevent="validateInputField('geoCode')"
                                  @paste="onPasteValidation('geoCode', $event)"
                                  @keypress="isWholeNumbers($event)"
                                  v-model="geoCode"
                                  :aria-label="fieldLabel('GEOCODE')"
                                >
                                <span v-if="hasErrorGeoCode" class="help is-danger">
                                  {{ msgGeoCode }}
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </template>
                  </es-form>
                  <div v-if="credentialInfoFieldsEnabled()">
                    <es-form :margin="{ margin: '0' }">
                      <template #es-form-header style="margin: 0">
                        <div class="header-container">
                          <div class="header-elements">
                            <font-awesome-icon :icon="['fas', 'diploma']" size="lg"/>
                          </div>
                          <div class="header-elements">
                            <h6 class="title">
                              Credential Information
                            </h6>
                          </div>
                        </div>
                      </template>
                      <template #es-form>
                        <div class="row removeUnderline" v-if= "fieldEnabled('HIGHEST_DEGREE')">
                          <div class="field is-horizontal">
                            <div class="field-label is-normal">
                              <label class="label">
                                {{ fieldLabel('HIGHEST_DEGREE') }}
                                <span class="label-optional has-text-grey-light"
                                  v-if= "!fieldRequired('HIGHEST_DEGREE')">
                                  (optional)
                                </span>
                              </label>
                            </div>
                            <div class="field-body">
                              <div class="field">
                                <div class="control">
                                  <es-select
                                    valueTpl="${description}"
                                    :setValues="activeHighestDegrees"
                                    keyValue="code"
                                    v-on:update:values="highestDegree = $event;
                                    validateSelectField('highestDegree')"
                                    v-bind:clearOut="true"
                                    ref="resetHighestDegree"
                                    :hasError="hasErrorHighestDegree"
                                    :errorMsg="msgHighestDegree"
                                  />
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                        <div
                          v-if= "fieldEnabled('SUBJECT_MATTER_AREA')"
                          class="row removeUnderline">
                          <div class="field is-horizontal">
                            <div class="field-label is-normal">
                              <label class="label">
                                  {{ fieldLabel('SUBJECT_MATTER_AREA') }}
                                <span class="label-optional has-text-grey-light"
                                  v-if= "!fieldRequired('SUBJECT_MATTER_AREA')">
                                  (optional)
                                </span>
                              </label>
                            </div>
                            <div class="field-body">
                              <div class="field">
                                <div class="control">
                                  <es-select
                                    valueTpl="${description}"
                                    :setValues="activeSubjectMatterAreas"
                                    keyValue="code"
                                    v-on:update:values="subjectMatterArea = $event;
                                    validateSelectField('subjectMatterArea')"
                                    v-bind:clearOut="true"
                                    ref="resetSubjectMatterArea"
                                    :hasError="hasErrorSubjectMatterArea"
                                    :errorMsg="msgSubjectMatterArea"
                                  />
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                        <div class="row removeUnderline" v-if= "fieldEnabled('DEGREE_PROVIDER')">
                          <div class="field is-horizontal">
                            <div class="field-label is-normal">
                              <label class="label">
                                  {{ fieldLabel('DEGREE_PROVIDER') }}
                                <span class="label-optional has-text-grey-light"
                                  v-if= "!fieldRequired('DEGREE_PROVIDER')">
                                  (optional)
                                </span>
                              </label>
                            </div>
                            <div class="field-body">
                              <div class="field">
                                <div class="control">
                                  <input
                                    class="input"
                                    type="text"
                                    :class="{'is-danger' : hasErrorDegreeProvider}"
                                    @keyup.prevent="validateInputField('degreeProvider')"
                                    @paste="onPasteValidation('degreeProvider')"
                                    v-model="degreeProvider"
                                    :aria-label="fieldLabel('DEGREE_PROVIDER')"
                                  >
                                </div>
                                <span v-if="hasErrorDegreeProvider" class="help is-danger">
                                  {{ msgDegreeProvider }}
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>
                        <div class="row removeUnderline" v-if= "fieldEnabled('CERTIFICATE_NUMBER')">
                          <div class="field is-horizontal">
                            <div class="field-label is-normal">
                              <label class="label">
                                {{ fieldLabel('CERTIFICATE_NUMBER') }}
                                <span class="label-optional has-text-grey-light"
                                  v-if= "!fieldRequired('CERTIFICATE_NUMBER')">
                                  (optional)
                                </span>
                              </label>
                            </div>
                            <div class="field-body">
                              <div class="field">
                                <div class="control">
                                  <input
                                    class="input"
                                    type="text"
                                    :class="{'is-danger' : hasErrorCertNumber}"
                                    @keyup.prevent="validateInputField('certNumber')"
                                    @paste="onPasteValidation('certNumber')"
                                    v-model="certNumber"
                                    :aria-label="fieldLabel('CERTIFICATE_NUMBER')"
                                  >
                                </div>
                                <span v-if="hasErrorCertNumber" class="help is-danger">
                                  {{ msgCertNumber }}
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>
                        <div class="row removeUnderline" v-if= "fieldEnabled('CERTIFICATE_TYPE')">
                          <div class="field is-horizontal">
                            <div class="field-label is-normal">
                              <label class="label">
                                {{ fieldLabel('CERTIFICATE_TYPE') }}
                                <span class="label-optional has-text-grey-light"
                                  v-if= "!fieldRequired('CERTIFICATE_TYPE')">
                                  (optional)
                                </span>
                              </label>
                            </div>
                            <div class="field-body">
                              <div class="field">
                                <div class="control">
                                  <es-select
                                    valueTpl="${description}"
                                    :setValues="activeCertificateTypes"
                                    keyValue="code"
                                    v-on:update:values="certType = $event;
                                    validateSelectField('certType')"
                                    v-bind:clearOut="true"
                                    ref="resetCertType"
                                    :hasError="hasErrorCertType"
                                    :errorMsg="msgCertType"
                                  />
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </template>
                    </es-form>
                  </div>
                </div>
                <div
                  v-if="otherInfoFieldsEnabled() || cdfFieldsEnabled()"
                  class="column master-record--column"
                >
                  <es-form :margin="{ margin: '0' }" v-if="otherInfoFieldsEnabled()">
                    <template #es-form-header style="margin: 0">
                      <div class="header-container">
                        <div class="header-elements">
                          <font-awesome-icon :icon="['fas', 'list-alt']" size="lg"/>
                        </div>
                        <div class="header-elements">
                          <h6 class="title">
                            Other Information
                          </h6>
                        </div>
                      </div>
                    </template>
                    <template #es-form>
                      <div class="row removeUnderline" v-if= "fieldEnabled('BIRTH_LOCATION')">
                        <div class="field is-horizontal">
                          <div class="field-label is-normal">
                            <label class="label">
                                {{ fieldLabel('BIRTH_LOCATION') }}
                              <span class="label-optional has-text-grey-light"
                                v-if= "!fieldRequired('BIRTH_LOCATION')">
                                (optional)
                              </span>
                            </label>
                          </div>
                          <div class="field-body">
                            <div class="field">
                              <div class="control">
                                <input
                                  class="input"
                                  type="text"
                                  :class="{'is-danger' : hasErrorBirthLocation}"
                                  v-model="birthLocation"
                                  @keyup.prevent="validateInputField('birthLocation')"
                                  @paste="onPasteValidation('birthLocation')"
                                  :aria-label="fieldLabel('BIRTH_LOCATION')"
                                >
                              </div>
                              <span v-if="hasErrorBirthLocation" class="help is-danger">
                                {{ msgBirthLocation }}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div class="row removeUnderline" v-if= "fieldEnabled('RESIDENCY_STATUS')">
                        <div class="field is-horizontal">
                          <div class="field-label is-normal">
                            <label class="label">
                              {{ fieldLabel('RESIDENCY_STATUS') }}
                              <span class="label-optional has-text-grey-light"
                                v-if= "!fieldRequired('RESIDENCY_STATUS')">
                                (optional)
                              </span>
                            </label>
                          </div>
                          <div class="field-body">
                            <div class="field">
                              <div class="control">
                                <es-select
                                  valueTpl="${description}"
                                  :setValues="activeResidencyStatuses"
                                  keyValue="code"
                                  v-on:update:values="residencyStatus = $event;
                                  validateSelectField('residencyStatus')"
                                  v-bind:clearOut="true"
                                  ref="resetResidencyStatus"
                                  :hasError="hasErrorResidencyStatus"
                                  :errorMsg="msgResidencyStatus"
                                />
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div
                        v-if= "fieldEnabled('RESIDENCY_EXPIRATION_DATE')"
                        class="row removeUnderline"
                      >
                        <div class="field is-horizontal">
                          <div class="field-label is-normal">
                            <label class="label">
                              {{ fieldLabel('RESIDENCY_EXPIRATION_DATE') }}
                              <span class="label-optional has-text-grey-light"
                                v-if= "!fieldRequired('RESIDENCY_EXPIRATION_DATE')">
                                (optional)
                              </span>
                            </label>
                          </div>
                          <div class="field-body">
                            <div class="field">
                              <div class="control">
                                <input
                                  class="input calendar"
                                  :class="{
                                    'placeholder': !residencyExpDate,
                                    'is-danger': hasErrorResidencyExpDate
                                  }"
                                  type="date"
                                  v-model="residencyExpDate"
                                  @change.prevent="validateDateField('residencyExpDate')"
                                  :aria-label="fieldLabel('RESIDENCY_EXPIRATION_DATE')"
                                />
                                <span v-if="hasErrorResidencyExpDate" class="help is-danger">
                                  {{ msgResidencyExpDate }}
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div class="row removeUnderline"
                        v-if= "fieldEnabled('MOTHERS_MAIDEN_NAME')">
                        <div class="field is-horizontal">
                          <div class="field-label is-normal">
                            <label class="label">
                              {{ fieldLabel('MOTHERS_MAIDEN_NAME') }}
                              <span class="label-optional has-text-grey-light"
                                v-if= "!fieldRequired('MOTHERS_MAIDEN_NAME')">
                                (optional)
                              </span>
                            </label>
                          </div>
                          <div class="field-body">
                            <div class="field">
                              <div class="control">
                                <input
                                  class="input"
                                  type="text"
                                  :class="{'is-danger' : hasErrorMothersMaidenName}"
                                  v-model="mothersMaidenName"
                                  @keyup.prevent="validateInputField('mothersMaidenName')"
                                  @paste="onPasteValidation('mothersMaidenName')"
                                  :aria-label="fieldLabel('MOTHERS_MAIDEN_NAME')"
                                >
                              </div>
                              <span v-if="hasErrorMothersMaidenName" class="help is-danger">
                                {{ msgMothersMaidenName }}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </template>
                  </es-form>
                  <es-form :margin="{ margin: '0' }" v-if="cdfFieldsEnabled()">
                    <template #es-form-header style="margin: 0">
                      <div class="header-container">
                        <div class="header-elements">
                          <font-awesome-icon :icon="['fas', 'database']" size="lg"/>
                        </div>
                        <div class="header-elements">
                          <h6 class="title">
                            Customer Defined Fields
                          </h6>
                        </div>
                      </div>
                    </template>
                    <template #es-form>
                      <div class="row removeUnderline" v-if= "fieldEnabled('cdf_1')">
                        <div class="field is-horizontal">
                          <div class="field-label is-normal">
                            <label class="label">
                              {{ fieldLabel('cdf_1') }}
                              <span class="label-optional has-text-grey-light"
                                v-if= "!fieldRequired('cdf_1')">
                                (optional)
                              </span>
                            </label>
                          </div>
                          <div class="field-body">
                            <div class="field">
                              <div class="control">
                                <input
                                  class="input"
                                  type="text"
                                  :class="{'is-danger' : hasErrorCdf1}"
                                  v-model="cdf_1"
                                  @keyup.prevent="validateInputField('cdf_1')"
                                  @paste="onPasteValidation('cdf_1')"
                                  :aria-label="fieldLabel('cdf_1')"
                                >
                              </div>
                              <span v-if="hasErrorCdf1" class="help is-danger">
                                {{ msgCdf1 }}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div class="row removeUnderline" v-if= "fieldEnabled('cdf_2')">
                        <div class="field is-horizontal">
                          <div class="field-label is-normal">
                            <label class="label">
                              {{ fieldLabel('cdf_2') }}
                              <span class="label-optional has-text-grey-light"
                                v-if= "!fieldRequired('cdf_2')">
                                (optional)
                              </span>
                            </label>
                          </div>
                          <div class="field-body">
                            <div class="field">
                              <div class="control">
                                <input
                                  class="input"
                                  type="text"
                                  :class="{'is-danger' : hasErrorCdf2}"
                                  v-model="cdf_2"
                                  @keyup.prevent="validateInputField('cdf_2')"
                                  @paste="onPasteValidation('cdf_2')"
                                  :aria-label="fieldLabel('cdf_2')"
                                >
                              </div>
                              <span v-if="hasErrorCdf2" class="help is-danger">
                                {{ msgCdf2 }}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div class="row removeUnderline" v-if= "fieldEnabled('cdf_3')">
                        <div class="field is-horizontal">
                          <div class="field-label is-normal">
                            <label class="label">
                              {{ fieldLabel('cdf_3') }}
                              <span class="label-optional has-text-grey-light"
                                v-if= "!fieldRequired('cdf_3')">
                                (optional)
                              </span>
                            </label>
                          </div>
                          <div class="field-body">
                            <div class="field">
                              <div class="control">
                                <input
                                  class="input"
                                  type="text"
                                  :class="{'is-danger' : hasErrorCdf3}"
                                  v-model="cdf_3"
                                  @keyup.prevent="validateInputField('cdf_3')"
                                  @paste="onPasteValidation('cdf_3')"
                                  :aria-label="fieldLabel('cdf_3')"
                                >
                                <span v-if="hasErrorCdf3" class="help is-danger">
                                  {{ msgCdf3 }}
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div class="row removeUnderline" v-if= "fieldEnabled('cdf_4')">
                        <div class="field is-horizontal">
                          <div class="field-label is-normal">
                            <label class="label">
                              {{ fieldLabel('cdf_4') }}
                              <span class="label-optional has-text-grey-light"
                                v-if= "!fieldRequired('cdf_4')">
                                (optional)
                              </span>
                            </label>
                          </div>
                          <div class="field-body">
                            <div class="field">
                              <div class="control">
                                <input
                                  class="input"
                                  type="text"
                                  :class="{'is-danger' : hasErrorCdf4}"
                                  v-model="cdf_4"
                                  @keyup.prevent="validateInputField('cdf_4')"
                                  @paste="onPasteValidation('cdf_4')"
                                  :aria-label="fieldLabel('cdf_4')"
                                >
                                <span v-if="hasErrorCdf4" class="help is-danger">
                                  {{ msgCdf4 }}
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div class="row removeUnderline" v-if= "fieldEnabled('cdf_5')">
                        <div class="field is-horizontal">
                          <div class="field-label is-normal">
                            <label class="label">
                              {{ fieldLabel('cdf_5') }}
                              <span class="label-optional has-text-grey-light"
                                v-if= "!fieldRequired('cdf_5')">
                                (optional)
                              </span>
                            </label>
                          </div>
                          <div class="field-body">
                            <div class="field">
                              <div class="control">
                                <input
                                  class="input"
                                  type="text"
                                  :class="{'is-danger' : hasErrorCdf5}"
                                  v-model="cdf_5"
                                  @keyup.prevent="validateInputField('cdf_5')"
                                  @paste="onPasteValidation('cdf_5')"
                                  :aria-label="fieldLabel('cdf_5')"
                                >
                                <span v-if="hasErrorCdf5" class="help is-danger">
                                  {{ msgCdf5 }}
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div class="row removeUnderline" v-if= "fieldEnabled('cdf_6')">
                        <div class="field is-horizontal">
                          <div class="field-label is-normal">
                            <label class="label">
                              {{ fieldLabel('cdf_6') }}
                              <span class="label-optional has-text-grey-light"
                                v-if= "!fieldRequired('cdf_6')">
                                (optional)
                              </span>
                            </label>
                          </div>
                          <div class="field-body">
                            <div class="field">
                              <div class="control">
                                <input
                                  class="input"
                                  type="text"
                                  :class="{'is-danger' : hasErrorCdf6}"
                                  v-model="cdf_6"
                                  @keyup.prevent="validateInputField('cdf_6')"
                                  @paste="onPasteValidation('cdf_6')"
                                  :aria-label="fieldLabel('cdf_6')"
                                >
                                <span v-if="hasErrorCdf6" class="help is-danger">
                                  {{ msgCdf6 }}
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div class="row removeUnderline" v-if= "fieldEnabled('cdf_7')">
                        <div class="field is-horizontal">
                          <div class="field-label is-normal">
                            <label class="label">
                              {{ fieldLabel('cdf_7') }}
                              <span class="label-optional has-text-grey-light"
                                v-if= "!fieldRequired('cdf_7')">
                                (optional)
                              </span>
                            </label>
                          </div>
                          <div class="field-body">
                            <div class="field">
                              <div class="control">
                                <input
                                  class="input"
                                  type="text"
                                  :class="{'is-danger' : hasErrorCdf7}"
                                  v-model="cdf_7"
                                  @keyup.prevent="validateInputField('cdf_7')"
                                  @paste="onPasteValidation('cdf_7')"
                                  :aria-label="fieldLabel('cdf_7')"
                                >
                                <span v-if="hasErrorCdf7" class="help is-danger">
                                  {{ msgCdf7 }}
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div class="row removeUnderline" v-if= "fieldEnabled('cdf_8')">
                        <div class="field is-horizontal">
                          <div class="field-label is-normal">
                            <label class="label">
                              {{ fieldLabel('cdf_8') }}
                              <span class="label-optional has-text-grey-light"
                                v-if= "!fieldRequired('cdf_8')">
                                (optional)
                              </span>
                            </label>
                          </div>
                          <div class="field-body">
                            <div class="field">
                              <div class="control">
                                <input
                                  class="input"
                                  type="text"
                                  :class="{'is-danger' : hasErrorCdf8}"
                                  v-model="cdf_8"
                                  @keyup.prevent="validateInputField('cdf_8')"
                                  @paste="onPasteValidation('cdf_8')"
                                  :aria-label="fieldLabel('cdf_8')"
                                >
                                <span v-if="hasErrorCdf8" class="help is-danger">
                                  {{ msgCdf8 }}
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div class="row removeUnderline" v-if= "fieldEnabled('cdf_9')">
                        <div class="field is-horizontal">
                          <div class="field-label is-normal">
                            <label class="label">
                              {{ fieldLabel('cdf_9') }}
                              <span class="label-optional has-text-grey-light"
                                v-if= "!fieldRequired('cdf_9')">
                                (optional)
                              </span>
                            </label>
                          </div>
                          <div class="field-body">
                            <div class="field">
                              <div class="control">
                                <input
                                  class="input calendar"
                                  :class="{
                                    'placeholder': !cdf_9_date,
                                    'is-danger' : hasErrorCdf9Date
                                  }"
                                  type="date"
                                  v-model="cdf_9_date"
                                  @change.prevent="validateDateField('cdf_9_date')"
                                  :aria-label="fieldLabel('cdf_9')"
                                />
                                <span v-if="hasErrorCdf9Date" class="help is-danger">
                                  {{ msgCdf9Date }}
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div class="row removeUnderline" v-if= "fieldEnabled('cdf_10')">
                        <div class="field is-horizontal">
                          <div class="field-label is-normal">
                            <label class="label">
                              {{ fieldLabel('cdf_10') }}
                              <span class="label-optional has-text-grey-light"
                                v-if= "!fieldRequired('cdf_10')">
                                (optional)
                              </span>
                            </label>
                          </div>
                          <div class="field-body">
                            <div class="field">
                              <div class="control">
                                <input
                                  class="input"
                                  type="text"
                                  :class="{'is-danger' : hasErrorCdf10Number }"
                                  v-model="cdf_10_number"
                                  @keyup.prevent="validateInputField('cdf_10_number')"
                                  @paste="onPasteValidation('cdf_10_number', $event)"
                                  @keypress="isWholeNumbers($event)"
                                  :aria-label="fieldLabel('cdf_10')"
                                >
                                <span v-if="hasErrorCdf10Number" class="help is-danger">
                                  {{ msgCdf10Number }}
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div class="row removeUnderline" v-if= "fieldEnabled('cdf_11')">
                        <div class="field is-horizontal">
                          <div class="field-label is-normal">
                            <label class="label">
                              {{ fieldLabel('cdf_11') }}
                              <span class="label-optional has-text-grey-light"
                                v-if= "!fieldRequired('cdf_11')">
                                (optional)
                              </span>
                            </label>
                          </div>
                          <div class="field-body">
                            <div class="field">
                              <div class="control">
                                <input v-if="!isLookup('cdf_11')"
                                  class="input"
                                  type="text"
                                  :class="{'is-danger' : hasErrorCdf11Code }"
                                  v-model="cdf_11_code"
                                  @keyup.prevent="validateInputField('cdf_11_code')"
                                  @paste="onPasteValidation('cdf_11_code')"
                                  :aria-label="fieldLabel('cdf_11')"
                                >
                                <es-select v-else
                                  valueTpl="${fieldValue}"
                                  :setValues="getCdfValidValues('cdf_11')"
                                  keyValue="fieldValue"
                                  v-on:update:values="cdf_11_code = $event;
                                    validateSelectField('cdf_11_code')"
                                  v-bind:clearOut="true"
                                  ref="resetCdf11"
                                  :hasError="hasErrorCdf11Code"
                                  :errorMsg="msgCdf11Code"
                                />
                                <span v-if="hasErrorCdf11Code" class="help is-danger">
                                  {{ msgCdf11Code }}
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </template>
                  </es-form>
                </div>

              </div>
              <div class="columns is-multiline is-marginless">
                <div class="column master-record--column">
                  <es-form :margin="{ margin: '0' }">
                    <template #es-form-header style="margin: 0">
                      <div class="header-container">
                        <div class="header-elements">
                          <font-awesome-icon :icon="['fas', 'map-marker-alt']" size="lg"/>
                        </div>
                        <div class="header-elements">
                          <h6 class="title">
                            Location Information
                          </h6>
                        </div>
                      </div>
                    </template>
                    <template #es-form>
                      <div class="row removeUnderline">
                        <div class="field is-horizontal">
                          <div class="field-label is-normal">
                            <label class="label">
                              {{ fieldLabel('SCHOOL_YEAR') }}
                            </label>
                          </div>
                          <div class="field-body">
                            <div class="field">
                              <div class="control">
                                <es-select
                                  :setValues="schoolYears"
                                  valueTpl="${year}"
                                  keyValue="id"
                                  v-on:update:values=
                                    "schoolYear = $event;
                                    validateSelectField('schoolYear')"
                                  ref="resetSchoolYear"
                                  :preSelected="preSelectedschoolYear"
                                  :disabled="allowedSchoolYear === 'Current Year Only'"
                                  :hasError="hasErrorSchoolYear"
                                  :errorMsg="msgSchoolYear"
                                />
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div class="row removeUnderline">
                        <div class="field is-horizontal">
                          <div class="field-label is-normal">
                            <label class="label">
                              {{ fieldLabel('DISTRICT_CODE') }}
                            </label>
                          </div>
                          <div class="field-body">
                            <div class="field">
                              <div class="control">
                                <es-select
                                  v-bind:search="true"
                                  api="districts"
                                  v-on:update:values="district = $event;
                                    validateSelectField('district')"
                                  :valueTplFn="(result) =>
                                    `${result.location.name} (${result.code})`"
                                  searchMinLength="2"
                                  keyValue="code"
                                  ref="resetDistrict"
                                  :preSelected="preSelDistrict"
                                  :hasError="hasErrorDistrict"
                                  :errorMsg="msgDistrict"
                                />
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div class="row removeUnderline">
                        <div class="field is-horizontal">
                          <div class="field-label is-normal">
                            <label class="label">
                              {{ fieldLabel('SCHOOL_CODE') }}
                            </label>
                          </div>
                          <div class="field-body">
                            <div class="field">
                              <div class="control">
                                <es-select
                                  :disabled="!district.length"
                                  v-bind:search="true"
                                  api="schools"
                                  :apiQueryParams="getDistrictParams"
                                  v-on:update:values="school = $event;
                                    validateSelectField('school')"
                                  :valueTplFn="(result) =>
                                    `${result.location.name} (${result.code})`"
                                  :subValueTplFn="(result) =>
                                    `${result.districtLocation.name}
                                      (<i>${result.districtCode}</i>)`"
                                  searchMinLength="2"
                                  keyValue="code"
                                  ref="resetSchool"
                                  :preSelected="preSelSchool"
                                  :hasError="hasErrorSchool"
                                  :errorMsg="msgSchool"
                                />
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div class="row removeUnderline" v-if= "fieldEnabled('RES_DISTRICT_CODE')">
                        <div class="field is-horizontal">
                          <div class="field-label is-normal">
                            <label class="label">
                              {{ fieldLabel('RES_DISTRICT_CODE') }}
                              <span class="label-optional has-text-grey-light"
                                v-if= "!fieldRequired('RES_DISTRICT_CODE')">
                                (optional)
                              </span>
                            </label>
                          </div>
                          <div class="field-body">
                            <div class="field">
                              <div class="control">
                                <es-select
                                  v-bind:search="true"
                                  api="districts"
                                  v-on:update:values="resDistrict = $event;
                                  validateSelectField('resDistrict')"
                                  :valueTplFn="(result) =>
                                    `${result.location.name} (${result.code})`"
                                  searchMinLength="2"
                                  keyValue="code"
                                  ref="resetResDistrict"
                                  :apiQueryParams="getAll"
                                  :hasError="hasErrorResDistrict"
                                  :errorMsg="msgResDistrict"
                                />
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div class="row removeUnderline" v-if="fieldEnabled('LOCAL_STUDENT_ID')" >
                        <div class="field is-horizontal">
                          <div class="field-label is-normal">
                            <label class="label">
                              {{ fieldLabel('LOCAL_STUDENT_ID') }}
                              <span class="label-optional has-text-grey-light"
                                v-if= "!fieldRequired('LOCAL_STUDENT_ID')">
                                (optional)
                              </span>
                            </label>
                          </div>
                          <div class="field-body">
                            <div class="field">
                              <div class="control">
                                <input
                                  class="input"
                                  type="text"
                                  :class="{'is-danger' : hasErrorLocalId}"
                                  v-model="localStudentId"
                                  @keyup.prevent="validateInputField('localStudentId')"
                                  @paste="onPasteValidation('localStudentId')"
                                  :aria-label="fieldLabel('LOCAL_STUDENT_ID')"
                                >
                              </div>
                              <span v-if="hasErrorLocalId" class="help is-danger">
                                {{ msgLocalId }}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div class="row removeUnderline">
                        <div class="field is-horizontal">
                          <div class="field-label is-normal">
                            <label class="label">
                              {{ fieldLabel('SOURCE_CODE') }}
                            </label>
                          </div>
                          <div class="field-body">
                            <div class="field">
                              <div class="control">
                                <es-select
                                  valueTpl="${description}"
                                  :setValues="activeSources"
                                  keyValue="code"
                                  v-on:update:values="source = $event;
                                    validateSelectField('source')"
                                  v-bind:clearOut="true"
                                  :preSelected="source"
                                  ref="resetSource"
                                  :hasError="hasErrorSource"
                                  :errorMsg="msgSource"
                                />
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div class="row removeUnderline" v-if= "fieldEnabled('ALT_STUDENT_ID')">
                        <div class="field is-horizontal">
                          <div class="field-label is-normal">
                            <label class="label">
                                {{ fieldLabel('ALT_STUDENT_ID') }}
                              <span class="label-optional has-text-grey-light"
                                v-if= "!fieldRequired('ALT_STUDENT_ID')">
                                (optional)
                              </span>
                            </label>
                          </div>
                          <div class="field-body">
                            <div class="field">
                              <div class="control">
                                <input
                                  class="input"
                                  type="text"
                                  :class="{'is-danger' : hasErrorAltId}"
                                  v-model="altStudentId"
                                  @keyup.prevent="validateInputField('altStudentId')"
                                  @paste="onPasteValidation('altStudentId')"
                                  :aria-label="fieldLabel('ALT_STUDENT_ID')"
                                >
                              </div>
                              <span v-if="hasErrorAltId" class="help is-danger">
                                {{ msgAltId }}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div class="row removeUnderline" v-if= "fieldEnabled('ALT_SOURCE_SIS_CODE')">
                        <div class="field is-horizontal">
                          <div class="field-label is-normal">
                            <label class="label">
                              {{ fieldLabel('ALT_SOURCE_SIS_CODE') }}
                              <span class="label-optional has-text-grey-light"
                                v-if= "!fieldRequired('ALT_SOURCE_SIS_CODE')">
                                (optional)
                              </span>
                            </label>
                          </div>
                          <div class="field-body">
                            <div class="field">
                              <div class="control">
                                <es-select
                                  valueTpl="${description}"
                                  :setValues="activeSources"
                                  keyValue="code"
                                  v-on:update:values="altSource = $event;
                                  validateSelectField('altSource')"
                                  v-bind:clearOut="true"
                                  ref="resetAlternateSource"
                                  :hasError="hasErrorAltSource"
                                  :errorMsg="msgAltSource"
                                />
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div class="row removeUnderline" v-if= "fieldEnabled('ACTIVE')">
                        <div class="field is-horizontal">
                          <div class="field-label is-normal">
                            <label class="label">
                              {{ fieldLabel('ACTIVE') }}
                              <span class="label-optional has-text-grey-light"
                                v-if= "!fieldRequired('ACTIVE')">
                                (optional)
                              </span>
                            </label>
                          </div>
                          <div class="field-body">
                            <div class="field">
                              <div class="control">
                                <es-select
                                  valueTpl="${description}"
                                  :setValues="statusOptions"
                                  keyValue="code"
                                  v-on:update:values="status = $event;
                                   validateSelectField('status')"
                                  v-bind:clearOut="true"
                                  :preSelected="status"
                                  ref="resetStatus"
                                  :hasError="hasErrorStatus"
                                  :errorMsg="msgStatus"
                                />
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div class="row removeUnderline" v-if= "fieldEnabled('SOURCE_REFRESH_DATE')">
                        <div class="field is-horizontal">
                          <div class="field-label is-normal">
                            <label class="label">
                              {{ fieldLabel('SOURCE_REFRESH_DATE') }}
                              <span class="label-optional has-text-grey-light"
                                v-if= "!fieldRequired('SOURCE_REFRESH_DATE')">
                                (optional)
                              </span>
                            </label>
                          </div>
                          <div class="field-body">
                            <div class="field">
                              <div class="control">
                                <input
                                  class="input calendar"
                                  :class="{
                                    'placeholder': !refreshDate ,
                                    'is-danger' : hasErrorRefreshDate
                                  }"
                                  type="date"
                                  v-model="refreshDate"
                                  @change.prevent="validateDateField('refreshDate')"
                                  :aria-label="fieldLabel('SOURCE_REFRESH_DATE')"
                                />
                                <span v-if="hasErrorRefreshDate" class="help is-danger">
                                {{ msgRefreshDate}}
                              </span>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div class="row removeUnderline" v-if= "fieldEnabled('LOCATION_COMMENT')">
                        <div class="field is-horizontal">
                          <div class="field-label is-normal">
                            <label class="label">
                              {{ fieldLabel('LOCATION_COMMENT') }}
                              <span class="label-optional has-text-grey-light"
                                v-if= "!fieldRequired('LOCATION_COMMENT')">
                                (optional)
                              </span>
                            </label>
                          </div>
                          <div class="field-body">
                            <div class="field">
                              <div class="control">
                                <textarea
                                  style="width: 100%"
                                  rows="3"
                                  :class="{'is-danger' : hasErrorLocComment}"
                                  v-model="locComment"
                                  @keyup.prevent="validateInputField('locComment')"
                                  @paste="onPasteValidation('locComment')"
                                  :aria-label="fieldLabel('LOCATION_COMMENT')"
                                />
                              </div>
                              <span v-if="hasErrorLocComment" class="help is-danger">
                                {{ msgLocComment }}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                      <template v-if="isEmployee">
                        <div class="row removeUnderline" v-if= "fieldEnabled('HIRE_DATE')">
                          <div class="field is-horizontal">
                            <div class="field-label is-normal">
                              <label class="label">
                                  {{ fieldLabel('HIRE_DATE') }}
                                <span class="label-optional has-text-grey-light"
                                  v-if= "!fieldRequired('HIRE_DATE')">
                                  (optional)
                                </span>
                              </label>
                            </div>
                            <div class="field-body">
                              <div class="field">
                                <div class="control">
                                  <input
                                    class="input calendar"
                                    :class="{
                                      'placeholder': !hireDate ,
                                      'is-danger' : hasErrorHireDate
                                    }"
                                    type="date"
                                    v-model="hireDate"
                                    @change.prevent="validateDateField('hireDate')"
                                    :aria-label="fieldLabel('HIRE_DATE')"
                                  />
                                  <span v-if="hasErrorHireDate" class="help is-danger">
                                    {{ msgHireDate }}
                                  </span>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                        <div class="row removeUnderline" v-if= "fieldEnabled('SALARY')">
                          <div class="field is-horizontal">
                            <div class="field-label is-normal">
                              <label class="label">
                                  {{ fieldLabel('SALARY') }}
                                <span class="label-optional has-text-grey-light"
                                  v-if= "!fieldRequired('SALARY')">
                                  (optional)
                                </span>
                              </label>
                            </div>
                            <div class="field-body">
                              <div class="field">
                                <div class="control">
                                  <div class="input-group">
                                    <div class="input-group-icon">
                                      $
                                    </div>
                                    <div class="input-group-area">
                                      <input
                                        class="input"
                                        type="text"
                                        :class="{'is-danger' : hasErrorSalary}"
                                        @keyup.prevent="validateField('salary')"
                                        @keypress="isFloatNumbers($event)"
                                        @blur="annSalaryFocusOut"
                                        @focus="annSalaryFocusIn"
                                        v-model="salary"
                                        :aria-label="fieldLabel('SALARY')"
                                      />
                                    </div>
                                    <span v-if="hasErrorSalary" class="help is-danger">
                                      {{ msgSalary }}
                                    </span>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                        <div class="row removeUnderline" v-if= "fieldEnabled('JOB_CLASS_CODE')">
                          <div class="field is-horizontal">
                            <div class="field-label is-normal">
                              <label class="label">
                                  {{ fieldLabel('JOB_CLASS_CODE') }}
                                <span class="label-optional has-text-grey-light"
                                  v-if= "!fieldRequired('JOB_CLASS_CODE')">
                                  (optional)
                                </span>
                              </label>
                            </div>
                            <div class="field-body">
                              <div class="field">
                                <div class="control">
                                  <es-select
                                    valueTpl="${description}"
                                    :setValues="activeTitles"
                                    keyValue="code"
                                    v-on:update:values= "jobClass= $event;
                                    validateSelectField('jobClass')"
                                    v-bind:clearOut="true"
                                    ref="resetJobClass"
                                    :hasError="hasErrorJobClass"
                                    :errorMsg="msgJobClass"
                                  />
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                        <div class="row removeUnderline" v-if= "fieldEnabled('JOB_ASSIGNMENT_1')">
                          <div class="field is-horizontal">
                            <div class="field-label is-normal">
                              <label class="label">
                                {{ fieldLabel('JOB_ASSIGNMENT_1') }}
                                <span class="label-optional has-text-grey-light"
                                  v-if= "!fieldRequired('JOB_ASSIGNMENT_1')">
                                  (optional)
                                </span>
                              </label>
                            </div>
                            <div class="field-body">
                              <div class="field">
                                <div class="control">
                                  <es-select
                                    valueTpl="${description}"
                                    :setValues="availableJobAssignments"
                                    keyValue="code"
                                    v-on:update:values="jobAssignment1 = $event;
                                    validateSelectField('jobAssignment1')"
                                    v-bind:clearOut="true"
                                    ref="resetJobAssignment1"
                                    :hasError="hasErrorJobAssignment1"
                                    :errorMsg="msgJobAssignment1"
                                  />
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                        <div class="row removeUnderline" v-if= "fieldEnabled('JOB_ASSIGNMENT_2')">
                          <div class="field is-horizontal">
                            <div class="field-label is-normal">
                              <label class="label">
                                  {{ fieldLabel('JOB_ASSIGNMENT_2') }}
                                <span class="label-optional has-text-grey-light"
                                  v-if= "!fieldRequired('JOB_ASSIGNMENT_2')">
                                  (optional)
                                </span>
                              </label>
                            </div>
                            <div class="field-body">
                              <div class="field">
                                <div class="control">
                                  <es-select
                                    valueTpl="${description}"
                                    :setValues="availableJobAssignments"
                                    keyValue="code"
                                    v-on:update:values="jobAssignment2 = $event;
                                    validateSelectField('jobAssignment2')"
                                    v-bind:clearOut="true"
                                    ref="resetJobAssignment2"
                                    :hasError="hasErrorJobAssignment2"
                                    :errorMsg="msgJobAssignment2"
                                  />
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                        <div class="row removeUnderline" v-if= "fieldEnabled('JOB_ASSIGNMENT_3')">
                          <div class="field is-horizontal">
                            <div class="field-label is-normal">
                              <label class="label">
                                  {{ fieldLabel('JOB_ASSIGNMENT_3') }}
                                <span class="label-optional has-text-grey-light"
                                  v-if= "!fieldRequired('JOB_ASSIGNMENT_3')">
                                  (optional)
                                </span>
                              </label>
                            </div>
                            <div class="field-body">
                              <div class="field">
                                <div class="control">
                                  <es-select
                                    valueTpl="${description}"
                                    :setValues="availableJobAssignments"
                                    keyValue="code"
                                    v-on:update:values="jobAssignment3 = $event;
                                    validateSelectField('jobAssignment3')"
                                    v-bind:clearOut="true"
                                    ref="resetJobAssignment3"
                                    :hasError="hasErrorJobAssignment3"
                                    :errorMsg="msgJobAssignment3"
                                  />
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                        <div class="row removeUnderline" v-if= "fieldEnabled('ITINERANT_FLAG')">
                          <div class="field is-horizontal">
                            <div class="field-label is-normal">
                              <label class="label">
                                  {{ fieldLabel('ITINERANT_FLAG') }}
                                <span class="label-optional has-text-grey-light"
                                  v-if= "!fieldRequired('ITINERANT_FLAG')">
                                  (optional)
                                </span>
                              </label>
                            </div>
                            <div class="field-body">
                              <div class="field">
                                <div class="control">
                                  <es-select
                                    valueTpl="${description}"
                                    :setValues="activeItinerantFlags"
                                    keyValue="code"
                                    v-on:update:values="itinerantFlag = $event;
                                    validateSelectField('itinerantFlag')"
                                    v-bind:clearOut="true"
                                    ref="resetItinerantFlag"
                                    :hasError="hasErrorItinerantFlag"
                                    :errorMsg="msgItinerantFlag"
                                  />
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </template>
                      <div
                        class="row removeUnderline"
                        v-if="fieldEnabled('GRADE_CODE') && isStudent"
                      >
                        <div class="field is-horizontal">
                          <div class="field-label is-normal">
                            <label class="label">
                              {{ fieldLabel('GRADE_CODE') }}
                              <span class="label-optional has-text-grey-light"
                                v-if= "!fieldRequired('GRADE_CODE')">
                                (optional)
                              </span>
                            </label>
                          </div>
                          <div class="field-body">
                            <div class="field">
                              <div class="control">
                                <es-select
                                  valueTpl="${description}"
                                  :setValues="activeGrades"
                                  keyValue="code"
                                  v-on:update:values="grade = $event; validateSelectField('grade')"
                                  v-bind:clearOut="true"
                                  ref="resetGrade"
                                  :hasError="hasErrorGrade"
                                  :errorMsg="msgGrade"
                                />
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </template>
                  </es-form>
                </div>
              </div>
              <div
                class="is-flex flex-justify--end flex-align--center"
                style="margin-right: 5.15rem; padding-bottom: 2rem"
              >
                <button
                  class="button button-link has-underline"
                  @click.prevent="reset"
                  style="margin-right: 1rem"
                >
                  Reset
                </button>
                <button
                  class="button is-primary"
                  :disabled="loadingMatch || !isPersonTypeEnabled"
                  @click.prevent="match"
                >
                  {{ properties['BUTTON_LABEL-ASSIGN_EXISTING'] }}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <match-results-pid
      ref="matchResultsPanel"
      :transaction="transaction"
      :loadingTransactions="loadingTransactions"
      @refresh="getTransactions"
      />
  </div>
</template>

<script>
import axios from 'axios';
import { mapActions, mapGetters } from 'vuex';
import { library } from '@fortawesome/fontawesome-svg-core';
import {
  faAddressBook,
  faDatabase,
  faDiploma,
  faListAlt,
  faMapMarker,
  faPhoneRotary,
  faPhone,
  faUser,
} from '@fortawesome/pro-solid-svg-icons';
import BreadcrumbsManager from '@/mixins/BreadcrumbManager';
import FieldLabels from '@/mixins/FieldLabels';
import DateUtils from '@/mixins/DateUtils';
import SchoolYearsAllowed from '@/mixins/SchoolYearsAllowed';
import ZipUtils from '@/mixins/ZipUtils';
import NotificationManager from '@/mixins/NotificationManager';
import EsException from '@/components/EsException.vue';
import EsForm from '@/components/EsForm.vue';
import EsHeader from '@/components/EsHeader.vue';
import EsLoader from '@/components/EsLoader.vue';
import EsSelect from '@/components/EsSelect.vue';
import MatchResultsPid from '@/components/MatchResultsPid.vue';
import NewMatchMessage from '@/components/NewMatchMessage.vue';
import PermissionManager from '@/mixins/PermissionManager';


library.add(
  faAddressBook,
  faDatabase,
  faDiploma,
  faListAlt,
  faMapMarker,
  faPhoneRotary,
  faPhone,
  faUser,
);
export default {
  mixins: [
    BreadcrumbsManager,
    FieldLabels,
    NotificationManager,
    DateUtils,
    SchoolYearsAllowed,
    ZipUtils,
    PermissionManager,
  ],
  components: {
    EsException,
    EsForm,
    EsHeader,
    EsLoader,
    EsSelect,
    MatchResultsPid,
    NewMatchMessage,
  },
  watch: {
    districts() {
      this.$refs.resetSchool.clear();
      this.hasErrorSchool = false;
      this.msgSchool = null;
    },
  },
  mounted() {
    this.onLoad();
  },
  asyncComputed: {
    async preSelDistrict() {
      const selDist = [];
      if (this.$route.params.districtCode) {
        const results = await axios.get(`districts/${this.$route.params.districtCode}`);
        selDist[0] = results.data;
        this.district[0] = results.data;
      }
      return selDist;
    },
    async preSelSchool() {
      const selSchool = [];
      if (this.$route.params.districtCode) {
        const results = await axios.get(`districts/${this.$route.params.districtCode}/schools/${this.$route.params.searchParams.schoolCode}`);
        selSchool[0] = results.data;
        this.school[0] = results.data;
      }
      return selSchool;
    },
  },
  computed: {
    ...mapGetters('lookups', [
      'activeCdfValidValues',
      'activeCertificateTypes',
      'activeEthnicities',
      'activeEthnicSubGroups',
      'activeGrades',
      'activeGenders',
      'activeHighestDegrees',
      'activeItinerantFlags',
      'activeJobAssignments',
      'activeRaces',
      'activeResidencyStatuses',
      'activeSources',
      'activeStates',
      'activeSubjectMatterAreas',
      'activeTitles',
      'defaultPersonType',
    ]),
    ...mapGetters('fieldLabelsModule', [
      'getFieldLabelByPersonType',
      'getFieldLengthsByPersonType',
      'getFieldEnabledForPersonType',
      'getFieldRequiredForPersonType',
    ]),
    ...mapGetters('propertiesModule', [
      'properties',
    ]),
    contextMode() {
      const identityControl = this.properties['IDENTITY_CONTROL-LIFETIME_IDS'];
      return JSON.parse(identityControl.toString().toLowerCase()) === false;
    },
    allowSinSsn() {
      return this.properties && (this.properties['GENERAL-SSN_ALLOW_S'] === 'true' || this.properties['GENERAL-SSN_ALLOW_S'] === true);
    },
    personCategory() {
      const pt = this.userPersonTypes.find(p => p.code === this.personType);
      if (pt && pt.category) {
        return pt.category;
      }
      return null;
    },
    availableRaces() {
      const allAvailableRaces = this.activeRaces.map(item => ({
        code: item.code,
        description: item.description,
      }));
      return allAvailableRaces.filter(race => !this.userSelectedRaces.includes(race.code));
    },
    availableJobAssignments() {
      const allJobAssignments = this.activeJobAssignments.map(item => ({
        code: item.code,
        description: item.description,
      }));
      return allJobAssignments.filter(j => !this.userSelectedJobAssignments.includes(j.code));
    },
    allowedSchoolYear() {
      return this.$store.state.propertiesModule.properties['RULE-SCHOOL_YEARS_ALLOWED'];
    },
    allSubSpecsArray() {
      return [].concat(...this.allSubmissionSpecs);
    },
    currentSchoolYear() {
      return this.$store.state.propertiesModule.properties['RULE-CURRENT_SCHOOL_YEAR'];
    },
    isStudent() {
      return this.personCategory && this.personCategory.toUpperCase() === 'STUDENT';
    },
    isEmployee() {
      return this.personCategory && this.personCategory.toUpperCase() === 'EMPLOYEE';
    },
    isPersonTypeEnabled() {
      return this.enabledSubmissionStatus.includes(this.personType) && this.personType !== null;
    },
    preSelectedschoolYear() {
      return [this.schoolYears.find(a => a.year === this.currentSchoolYear)];
    },
    enabledSubmissionStatus() {
      if (this.allSubSpecsArray) {
        return this.allSubSpecsArray
          .filter(d => d.purpose === 'ASSIGN' && d.enabled === true)
          .map(p => p.personType.code);
      }
      return null;
    },
    getAll() {
      return {
        allDistricts: true,
      };
    },
    getDistrictParams() {
      const params = {};
      params.districtCode = this.district.map(d => d.code);
      return params;
    },
    futureBirthYearsAllowed() {
      return this.$store.state.propertiesModule.properties['RULE-FUTURE_BIRTH_YEARS'] !== 'false';
    },
    sortedPersonTypesOptions() {
      return this.userPersonTypes.slice().sort((a, b) => {
        if (a.name.toUpperCase() < b.name.toUpperCase()) {
          return -1;
        }
        if (a.name.toUpperCase() > b.name.toUpperCase()) {
          return 1;
        }
        return 0;
      });
    },
    userSelectedRaces() {
      const userSelection = [
        this.race1,
        this.race2,
        this.race3,
        this.race4,
        this.race5,
      ];
      const selectedRaceCodes = [];
      userSelection.forEach((selection) => {
        const raceCodes = selection.map(r => r.code);
        selectedRaceCodes.push(...raceCodes);
      });
      return selectedRaceCodes;
    },
    userSelectedJobAssignments() {
      const userSelection = [
        this.jobAssignment1,
        this.jobAssignment2,
        this.jobAssignment3,
      ];
      const selectedJobAssignmentCodes = [];
      userSelection.forEach((selection) => {
        const jobAssignmentCodes = selection.map(j => j.code);
        selectedJobAssignmentCodes.push(...jobAssignmentCodes);
      });
      return selectedJobAssignmentCodes;
    },
    submissionPurposeRequiresStateId() {
      return this.submissionPurpose === '007';
    },
  },
  created() {
    this.setBreadcrumb([
      {
        text: 'Home',
        to: {
          name: 'pidDashboard',
        },
      },
      { text: 'Add a New Person', to: {} },
    ]);
  },
  data() {
    return this.initialData();
  },
  methods: {
    ...mapActions('lookups', [
      'loadCertificateTypes',
      'loadGrades',
      'loadGenders',
      'loadEthnicities',
      'loadEthnicSubGroups',
      'loadHighestDegrees',
      'loadJobAssignments',
      'loadRaces',
      'loadTitles',
      'loadSources',
      'loadStates',
      'loadPersonTypes',
      'loadResidencyStatuses',
      'loadSubjectMatterAreas',
      'loadPidSchoolYears',
      'loadCDFValidValues',
      'loadItinerantFlags',
    ]),
    annSalaryFocusIn() {
      if (this.salaryTemporary !== null) {
        // Unformat  Salary value before user starts modifying it
        this.salary = this.salaryTemporary.toString();
      }
    },
    annSalaryFocusOut() {
      if (this.salary !== null) {
        // Recalculate the  Salary after ignoring the digits and "." in user input
        this.salaryTemporary = parseFloat(this.salary.replace(/[^\d\.]/g, "")); // eslint-disable-line
      }
      if (Number.isNaN(this.salaryTemporary)) {
        // Ensure that value is not NaN. If so, initialize it to 'this.salary (NULL)'.
        // This happens if user provides a blank input
        this.salaryTemporary = this.salary;
      }
      if (this.salaryTemporary !== null) {
        // Format display value based on calculated  Salary
        this.salary = this.salaryTemporary.toFixed(2).replace(/(\d)(?=(\d{3})+(?:\.,\d+)?$)/g, '$1,');
      }
    },
    checkFieldValidations() {
      this.validateInputField('firstName');
      this.validateInputField('middleName');
      this.validateInputField('lastName');
      this.validateInputField('suffix');
      this.validateInputField('altLastName');
      this.validateInputField('nickName');
      this.validateInputField('fullName');
      this.validateField('gender');
      this.validateField('dateOfBirth');
      this.validateField('ssn');
      this.validateSelectField('ethnicity');
      this.validateSelectField('race1');
      this.validateSelectField('race2');
      this.validateSelectField('race3');
      this.validateSelectField('race4');
      this.validateSelectField('race5');
      this.validateSelectField('ethnicSubGroup');
      this.validateInputField('stateId');

      this.validateInputField('address1');
      this.validateInputField('address2');
      this.validateInputField('city');
      this.validateField('zip');
      this.validateInputField('geoCode');
      this.validateSelectField('state');

      this.validateSelectField('highestDegree');
      this.validateSelectField('subjectMatterArea');
      this.validateInputField('degreeProvider');
      this.validateInputField('certNumber');
      this.validateSelectField('certType');

      this.validateInputField('birthLocation');
      this.validateSelectField('residencyStatus');
      this.validateDateField('residencyExpDate');
      this.validateInputField('mothersMaidenName');

      this.validateSelectField('schoolYear');
      this.validateSelectField('district');
      this.validateSelectField('school');
      this.validateSelectField('resDistrict');
      this.validateInputField('localStudentId');
      this.validateSelectField('source');
      this.validateInputField('altStudentId');
      this.validateSelectField('altSource');
      this.validateSelectField('status');
      this.validateDateField('refreshDate');
      this.validateInputField('locComment');

      this.validateInputField('cdf_1');
      this.validateInputField('cdf_2');
      this.validateInputField('cdf_3');
      this.validateInputField('cdf_4');
      this.validateInputField('cdf_5');
      this.validateInputField('cdf_6');
      this.validateInputField('cdf_7');
      this.validateInputField('cdf_8');
      this.validateDateField('cdf_9_date');
      this.validateInputField('cdf_10_number');
      this.validateInputField('cdf_11_code');

      if (this.isStudent) {
        this.validateSelectField('grade');
      }
      // validation for staff fields
      if (this.isEmployee) {
        this.validateSelectField('jobClass');
        this.validateSelectField('jobAssignment1');
        this.validateSelectField('jobAssignment2');
        this.validateSelectField('jobAssignment3');
        this.validateSelectField('itinerantFlag');
        this.validateField('salary');
        this.validateDateField('hireDate');
      }

      this.validateSubmissionPurpose();

      const err = this.hasErrorFirstName
        || this.hasErrorMiddleName
        || this.hasErrorLastName
        || this.hasErrorSuffix
        || this.hasErrorAltLastName
        || this.hasErrorNickName
        || this.hasErrorFullName
        || this.hasErrorGender
        || this.hasErrorDateOfBirth
        || this.hasErrorSSN
        || this.hasErrorEthnicity
        || this.hasErrorRaceEthnicCode
        || this.hasErrorRace2Code
        || this.hasErrorRace3Code
        || this.hasErrorRace4Code
        || this.hasErrorRace5Code
        || this.hasErrorEthnicSubGroup
        || this.hasErrorStateId
        || this.hasErrorAddress1
        || this.hasErrorAddress2
        || this.hasErrorCity
        || this.hasErrorState
        || this.hasErrorZip
        || this.hasErrorGeoCode
        || this.hasErrorHighestDegree
        || this.hasErrorSubjectMatterArea
        || this.hasErrorDegreeProvider
        || this.hasErrorCertNumber
        || this.hasErrorCertType
        || this.hasErrorBirthLocation
        || this.hasErrorResidencyStatus
        || this.hasErrorResidencyExpDate
        || this.hasErrorMothersMaidenName
        || this.hasErrorCdf1
        || this.hasErrorCdf2
        || this.hasErrorCdf3
        || this.hasErrorCdf4
        || this.hasErrorCdf5
        || this.hasErrorCdf6
        || this.hasErrorCdf7
        || this.hasErrorCdf8
        || this.hasErrorCdf9Date
        || this.hasErrorCdf10Number
        || this.hasErrorCdf11Code
        || this.hasErrorSchoolYear
        || this.hasErrorDistrict
        || this.hasErrorSchool
        || this.hasErrorResDistrict
        || this.hasErrorResidencyStatus
        || this.hasErrorLocalId
        || this.hasErrorSource
        || this.hasErrorAltId
        || this.hasErrorAltSource
        || this.hasErrorStatus
        || this.hasErrorRefreshDate
        || this.hasErrorLocComment
        || this.hasErrorCdf1
        || this.hasErrorCdf2
        || this.hasErrorCdf3
        || this.hasErrorCdf4
        || this.hasErrorCdf5
        || this.hasErrorCdf6
        || this.hasErrorCdf7
        || this.hasErrorCdf8
        || this.hasErrorCdf9Date
        || this.hasErrorCdf10Number
        || this.hasErrorCdf11Code
        || this.hasErrorJobClass
        || this.hasErrorJobAssignment1
        || this.hasErrorJobAssignment2
        || this.hasErrorJobAssignment3
        || this.hasErrorHireDate
        || this.hasErrorSalary
        || this.hasErrorItinerantFlag
        || this.hasErrorGrade;
      if (err) {
        this.dataSubmitted = false;
        this.showError(this.formErrorMsg);
      } else {
        this.resetNotifications();
      }
      return err;
    },
    cdfFieldsEnabled() {
      return this.isAnyFieldEnabled(
        ['cdf_1', 'cdf_2', 'cdf_3', 'cdf_4', 'cdf_5', 'cdf_6', 'cdf_7', 'cdf_8', 'cdf_9', 'cdf_10', 'cdf_11'],
      );
    },
    contactInfoFieldsEnabled() {
      return this.isAnyFieldEnabled(
        ['STREET_1', 'STREET_2', 'CITY', 'STATE_CODE', 'ZIP', 'GEOCODE'],
      );
    },
    credentialInfoFieldsEnabled() {
      return this.isAnyFieldEnabled(
        ['HIGHEST_DEGREE', 'SUBJECT_MATTER_AREA', 'DEGREE_PROVIDER', 'CERTIFICATE_NUMBER', 'CERTIFICATE_TYPE'],
      );
    },
    fieldLabel(code) {
      return this.getFieldLabelByPersonType(code, 'ASSIGN', this.personType);
    },
    fieldLength(code) {
      return this.getFieldLengthsByPersonType(code, 'ASSIGN', this.personType);
    },
    fieldEnabled(code) {
      return this.getFieldEnabledForPersonType(code, 'ASSIGN', this.personType);
    },
    fieldRequired(code) {
      return this.getFieldRequiredForPersonType(code, 'ASSIGN', this.personType);
    },
    getCdfValidValues(cdf) {
      if (this.activeCdfValidValues) {
        return this.activeCdfValidValues.filter(v => v.fieldCode === cdf);
      }
      return [];
    },
    async getTransactions(submissionId) {
      // Use the provided submissionId, or fall back to lastSubmissionId
      this.loadingTransactions = true;
      const id = submissionId || this.lastSubmissionId;
      if (!id) return; // No submissionId available
      try {
        const res = await axios.get(`personid/transactions/${id}`);
        this.transaction = res.data;
        this.lastSubmissionId = this.transaction.pidSubmissionId;
      } catch (error) {
        // continue regardless of error
      } finally {
        this.loadingTransactions = false;
      }
    },
    initialData() {
      return {
        allSubmissionSpecs: [],
        // array fields for setDataElements()
        statusOptions: [],
        submissionPurposes: [],
        // error message variables
        msgAddress1: null,
        msgAddress2: null,
        msgAltId: null,
        msgAltSource: null,
        msgAltLastName: null,
        msgSalary: null,
        msgBirthLocation: null,
        msgCertNumber: null,
        msgCertType: null,
        msgCity: null,
        msgCdf1: null,
        msgCdf2: null,
        msgCdf3: null,
        msgCdf4: null,
        msgCdf5: null,
        msgCdf6: null,
        msgCdf7: null,
        msgCdf8: null,
        msgCdf9Date: null,
        msgCdf10Number: null,
        msgCdf11Code: null,
        msgDateOfBirth: null,
        msgDegreeProvider: null,
        msgDistrict: null,
        msgEthnicity: null,
        msgEthnicSubGroup: null,
        msgFirstName: null,
        msgGender: null,
        msgGeoCode: null,
        msgGrade: null,
        msgHireDate: null,
        msgHighestDegree: null,
        msgItinerantFlag: null,
        msgJobClass: null,
        msgJobAssignment1: null,
        msgJobAssignment2: null,
        msgJobAssignment3: null,
        msgLastName: null,
        msgLocalId: null,
        msgLocComment: null,
        msgMiddleName: null,
        msgMothersMaidenName: null,
        msgNickName: null,
        msgFullName: null,
        msgResDistrict: null,
        msgResidencyStatus: null,
        msgRaceEthnicCode: null,
        msgRace2Code: null,
        msgRace3Code: null,
        msgRace4Code: null,
        msgRace5Code: null,
        msgResidencyExpDate: null,
        msgRefreshDate: null,
        msgSchool: null,
        msgSchoolYear: null,
        msgState: null,
        msgSubjectMatterArea: null,
        msgSource: null,
        msgStatus: null,
        msgStateId: null,
        msgSSN: null,
        msgSuffix: null,
        msgZip: null,

        // error variables
        hasErrorAddress1: false,
        hasErrorAddress2: false,
        hasErrorAltId: false,
        hasErrorAltSource: false,
        hasErrorAltLastName: false,
        hasErrorSalary: false,
        hasErrorBirthLocation: false,
        hasErrorCertNumber: false,
        hasErrorCertType: false,
        hasErrorCity: false,
        hasErrorCdf1: false,
        hasErrorCdf2: false,
        hasErrorCdf3: false,
        hasErrorCdf4: false,
        hasErrorCdf5: false,
        hasErrorCdf6: false,
        hasErrorCdf7: false,
        hasErrorCdf8: false,
        hasErrorCdf9Date: false,
        hasErrorCdf10Number: false,
        hasErrorCdf11Code: false,
        hasErrorDateOfBirth: false,
        hasErrorDegreeProvider: false,
        hasErrorDistrict: false,
        hasErrorEthnicity: false,
        hasErrorEthnicSubGroup: false,
        hasErrorFirstName: false,
        hasErrorGender: false,
        hasErrorGeoCode: false,
        hasErrorGrade: false,
        hasErrorHireDate: false,
        hasErrorHighestDegree: false,
        hasErrorItinerantFlag: false,
        hasErrorJobClass: false,
        hasErrorJobAssignment1: false,
        hasErrorJobAssignment2: false,
        hasErrorJobAssignment3: false,
        hasErrorLastName: false,
        hasErrorLocalId: false,
        hasErrorLocComment: false,
        hasErrorMiddleName: false,
        hasErrorMothersMaidenName: false,
        hasErrorNickName: false,
        hasErrorFullName: false,
        hasErrorResDistrict: false,
        hasErrorResidencyStatus: false,
        hasErrorRaceEthnicCode: false,
        hasErrorRace2Code: false,
        hasErrorRace3Code: false,
        hasErrorRace4Code: false,
        hasErrorRace5Code: false,
        hasErrorResidencyExpDate: false,
        hasErrorRefreshDate: false,
        hasErrorState: false,
        hasErrorSubjectMatterArea: false,
        hasErrorSchool: false,
        hasErrorSchoolYear: false,
        hasErrorSource: false,
        hasErrorStatus: false,
        hasErrorSSN: false,
        hasErrorStateId: false,
        hasErrorSuffix: false,
        hasErrorZip: false,

        // form fields variables
        assignmentFields: null,
        address1: null,
        address2: null,
        altStudentId: null,
        altLastName: null,
        altSource: [],
        salary: null,
        birthLocation: null,
        cdf_1: null,
        cdf_2: null,
        cdf_3: null,
        cdf_4: null,
        cdf_5: null,
        cdf_6: null,
        cdf_7: null,
        cdf_8: null,
        cdf_9_date: null,
        cdf_10_number: null,
        cdf_11_code: null,
        certNumber: null,
        certType: [],
        city: null,
        dateOfBirth: null,
        degreeProvider: null,
        district: [],
        ethnicSubGroup: [],
        ethnicity: [],
        firstName: null,
        gender: null,
        geoCode: null,
        grade: [],
        highestDegree: [],
        hireDate: null,
        itinerantFlag: [],
        jobAssignment1: [],
        jobAssignment2: [],
        jobAssignment3: [],
        jobClass: [],
        lastName: null,
        lastSubmissionId: null,
        localStudentId: null,
        locComment: null,
        middleName: null,
        mothersMaidenName: null,
        nickName: null,
        fullName: null,
        personType: null,
        race1: [],
        race2: [],
        race3: [],
        race4: [],
        race5: [],
        refreshDate: null,
        residencyExpDate: null,
        residencyStatus: [],
        resDistrict: [],
        schoolYear: [],
        school: [],
        source: [],
        ssn: null,
        state: [],
        stateId: null,
        status: [],
        subjectMatterArea: [],
        suffix: null,
        schoolCode: null,
        submissionPurpose: '001', // default to 001 for case when user does not have permission for other submission purposes
        districtCode: null,
        zip: null,

        // global variables
        transaction: {},
        dataSubmitted: false,
        loading: false,
        loadingError: false,
        loadingMatch: false,
        loadingTransactions: false,
        userPersonTypes: this.$store.state.user.userPersonTypes
          .map(item => item.personType).filter(a => a.status === true),
        userPersonTypesCodes: null,

        // least important variables
        salaryTemporary: null,
        ssnTemporary: null,
        formErrorMsg: 'There were some errors. Please fix the errors highlighted in the fields below.',
      };
    },
    isFloatNumbers(evt) {
      // Listen to the key event when a keyboard key is pressed
      evt = (evt) || window.event; // eslint-disable-line
      // Store the ASCII value of the pressed key in 'charCode'
      const charCode = (evt.which) ? evt.which : evt.keyCode;
      // Prevent user from entering any values except digits and '.'
      if (charCode > 31 && (charCode < 48 || charCode > 57) && charCode !== 46) {
        evt.preventDefault();
      }
    },
    isSSN(evt) {
      // Listen to the key event when a keyboard key is pressed
      evt = (evt) || window.event; // eslint-disable-line
      // Store the ASCII value of the pressed key in 'charCode'
      const charCode = (evt.which) ? evt.which : evt.keyCode;
      // Prevent user from entering any values except digits, s, S and dash
      if (charCode > 31 && (charCode < 48 || charCode > 57) && charCode !== 83
          && charCode !== 45 && charCode !== 115) {
        evt.preventDefault();
      }
    },
    isWholeNumbers(evt) {
      // Listen to the key event when a keyboard key is pressed
      evt = (evt) || window.event; // eslint-disable-line
      // Store the ASCII value of the pressed key in 'charCode'
      const charCode = (evt.which) ? evt.which : evt.keyCode;
      // Prevent user from entering any values except digits
      if (charCode > 31 && (charCode < 48 || charCode > 57)) {
        evt.preventDefault();
      }
    },
    isLookup(cdf) {
      const l = this.getCdfValidValues(cdf);
      return l && l.length > 0;
    },
    isAnyFieldEnabled(item) {
      // eslint-disable-next-line no-shadow
      return item.find(item => this.fieldEnabled(item));
    },
    async loadSubmissionSpecs() {
      const personTypeIds = this.userPersonTypes.map(item => item.id);
      try {
        this.loading = true;
        this.loadingError = false;
        // build promises to get submission specs info per person type
        const submissionSpecResponse = await Promise.all([...personTypeIds]
          .map(id => axios.get(`person-types/${id}/submission-specs/`)));
        // use the response from above and push them
        submissionSpecResponse.forEach((s) => {
          this.allSubmissionSpecs.push(s.data);
        });
      } catch (err) {
        this.loadingError = true;
      } finally {
        this.loading = false;
      }
    },
    async loadSubmissionPurposes() {
      try {
        this.loading = true;
        this.loadingError = false;
        // call API to get submission purposes
        const params = { category: 'MATCH' };
        const resp = await axios.get('/submission-purposes/', { params });
        this.submissionPurposes = resp.data;
      } catch (err) {
        this.loadingError = true;
      } finally {
        this.loading = false;
      }
    },
    async match() {
      const cdf11 = Array.isArray(this.cdf_11_code)
        ? this.cdf_11_code[0].fieldValue : this.cdf_11_code;
      if (this.checkFieldValidations()) return;
      try {
        this.loadingMatch = true;
        const res = await axios.post('/personid/submissions', ({
          active: this.fieldEnabled('ACTIVE') && this.status.length > 0 ? this.status[0].code : null,
          akaName: this.nickName,
          fullName: this.fullName,
          altStudentId: this.altStudentId,
          altSourceSisCode: this.altSource && this.altSource.length > 0
            ? this.altSource[0].code : null,
          birthLocation: this.birthLocation,
          certificateNumber: this.certNumber,
          certificateType: this.certType && this.certType.length > 0
            ? this.certType[0].code : null,
          city: this.city,
          comments: this.locComment,
          dateOfBirth: this.dateOfBirth,
          degreeProvider: this.degreeProvider,
          districtCode: this.district && this.district.length > 0 ? this.district[0].code : null,
          ethnicityIndicator: this.ethnicity && this.ethnicity.length > 0
            ? this.ethnicity[0].code : null,
          ethnicSubGroup: this.ethnicSubGroup && this.ethnicSubGroup.length > 0
            ? this.ethnicSubGroup[0].code : null,
          firstName: this.firstName,
          middleName: this.middleName,
          lastName: this.lastName,
          suffix: this.suffix,
          previousLastName: this.altLastName,
          genderCode: this.gender,
          socialSecurityNumber: this.ssn && this.ssn.length > 0 ? this.ssn.replace(/[-]/g, '') : null,
          raceEthnicCode: this.race1 && this.race1.length > 0 ? this.race1[0].code : null,
          race2Code: this.race2 && this.race2.length > 0 ? this.race2[0].code : null,
          race3Code: this.race3 && this.race3.length > 0 ? this.race3[0].code : null,
          race4Code: this.race4 && this.race4.length > 0 ? this.race4[0].code : null,
          race5Code: this.race5 && this.race5.length > 0 ? this.race5[0].code : null,
          stateStudentId: this.stateId,
          street1: this.address1,
          street2: this.address2,
          stateCode: this.state && this.state.length > 0 ? this.state[0].code : null,
          zip: this.zip,
          geocode: this.geoCode,
          highestDegree: this.highestDegree && this.highestDegree.length > 0
            ? this.highestDegree[0].code : null,
          subjectMatterArea: this.subjectMatterArea && this.subjectMatterArea.length > 0
            ? this.subjectMatterArea[0].code : null,
          residencyStatus: this.residencyStatus && this.residencyStatus.length > 0
            ? this.residencyStatus[0].code : null,
          residencyExpirationDate: this.residencyExpDate,
          mothersMaidenName: this.mothersMaidenName,
          studentCdf: {
            cdf_1: this.cdf_1,
            cdf_2: this.cdf_2,
            cdf_3: this.cdf_3,
            cdf_4: this.cdf_4,
            cdf_5: this.cdf_5,
            cdf_6: this.cdf_6,
            cdf_7: this.cdf_7,
            cdf_8: this.cdf_8,
            cdf_9: this.cdf_9_date,
            cdf_10: this.cdf_10_number,
            cdf_11: cdf11,
          },
          schoolYear: this.schoolYear && this.schoolYear.length > 0
            ? this.schoolYear[0].year : null,
          schoolCode: this.school && this.school.length > 0 ? this.school[0].code : null,
          resDistrictCode: this.resDistrict && this.resDistrict.length > 0
            ? this.resDistrict[0].code : null,
          localStudentId: this.localStudentId,
          sourceCode: this.source && this.source.length > 0 ? this.source[0].code : null,
          sourceRefreshDate: this.refreshDate,
          gradeCode: this.grade && this.grade.length > 0 ? this.grade[0].code : null,
          hireDate: this.hireDate,
          salary: this.salary,
          jobClassCode: this.jobClass && this.jobClass.length > 0 ? this.jobClass[0].code : null,
          jobAssignment1: this.jobAssignment1 && this.jobAssignment1.length > 0
            ? this.jobAssignment1[0].code : null,
          jobAssignment2: this.jobAssignment2 && this.jobAssignment2.length > 0
            ? this.jobAssignment2[0].code : null,
          jobAssignment3: this.jobAssignment3 && this.jobAssignment3.length > 0
            ? this.jobAssignment3[0].code : null,
          itinerantFlag: this.itinerantFlag && this.itinerantFlag.length > 0
            ? this.itinerantFlag[0].code : null,
          personType: this.personType,
          submissionPurpose: this.submissionPurpose,
        }));
        const SearchResultPid = res.data;
        if (SearchResultPid.errorString) {
          this.parseValidationErrors(SearchResultPid.errorString);
          this.dataSubmitted = false;
        } else {
          const { submissionId } = res.data.request.target;
          await this.getTransactions(submissionId);
          this.dataSubmitted = true;
        }
      } catch (error) {
        this.loadingError = true;
        if (error.res.status === 500) {
          this.messages.push('Network Error');
        } else {
          this.messages.push(error.res.data.message);
        }
        this.hasError = true;
        this.showNotification('danger', this.messages);
      } finally {
        this.loadingMatch = false;
      }
    },
    async onLoad() {
      this.loading = true;
      this.loadingError = false;
      try {
        await Promise.all([
          this.loadCertificateTypes(),
          this.loadGrades(),
          this.loadGenders(),
          this.loadEthnicities(),
          this.loadEthnicSubGroups(),
          this.loadHighestDegrees(),
          this.loadRaces(),
          this.loadTitles(),
          this.loadSources(),
          this.loadStates(),
          this.loadPersonTypes(),
          this.loadResidencyStatuses(),
          this.loadSubjectMatterAreas(),
          this.loadJobAssignments(),
          this.loadPidSchoolYears(),
          this.loadCDFValidValues(),
          this.loadItinerantFlags(),
        ]);
        this.userPersonTypesCodes = this.userPersonTypes
          .map(item => item.code);
        this.personType = this.userPersonTypesCodes.includes(this.defaultPersonType)
          ? this.defaultPersonType : this.userPersonTypesCodes[0];
        this.source = this.activeSources.filter(s => s.isDefault === true);
        this.loadSubmissionSpecs();
        this.loadSubmissionPurposes();
        this.setDataElements();
        this.setSearchParams();
      } catch (err) {
        this.loadingError = true;
      } finally {
        this.loading = false;
      }
    },
    otherInfoFieldsEnabled() {
      return this.isAnyFieldEnabled(
        ['BIRTH_LOCATION', 'RESIDENCY_STATUS', 'RESIDENCY_EXPIRATION_DATE', 'MOTHERS_MAIDEN_NAME'],
      );
    },
    onPasteValidation(fieldName, event) {
      if (event && event.clipboardData) {
        // Filtering logic (only digits)
        event.preventDefault();
        const pastedData = event.clipboardData.getData('text');
        const digitsOnly = pastedData.replace(/\D+/g, '');
        this[fieldName] = digitsOnly;
      }

      // Run validation after paste completes
      this.$nextTick(() => {
        this.validateInputField(fieldName);
      });
    },
    personTypeFieldReset() {
      if (this.isEmployee) {
        this.resetStaff();
      } else if (this.isStudent) {
        this.resetEsSelectStudent();
      }
    },
    parseValidationErrors(data) {
      if (data && !this.checkFieldValidations()) {
        this.showError(this.formErrorMsg);
        const messages = data.split(',');
        const errors = messages.map((item) => {
          const index = item.trim().indexOf(':');
          const displayMessage = item.trim().substring(index + 1);
          const e = { field: item.trim().split(' ')[1], error: displayMessage };
          return e;
        });
        const obj = {
          // Person Information Validation Errors
          FIRST_NAME: ['hasErrorFirstName', 'msgFirstName'],
          LAST_NAME: ['hasErrorLastName', 'msgLastName'],
          MIDDLE_NAME: ['hasErrorMiddleName', 'msgMiddleName'],
          SUFFIX: ['hasErrorSuffix', 'msgSuffix'],
          PREVIOUS_LAST_NAME: ['hasErrorAltLastName', 'msgAltLastName'],
          AKA_NAME: ['hasErrorNickName', 'msgNickName'],
          FULL_NAME: ['hasErrorFullName', 'msgFullName'],
          GENDER_CODE: ['hasErrorGender', 'msgGender'],
          DATE_OF_BIRTH: ['hasErrorDateOfBirth', 'msgDateOfBirth'],
          SOCIAL_SECURITY_NUMBER: ['hasErrorSSN', 'msgSSN'],
          ETHNICITY_INDICATOR: ['hasErrorEthnicity', 'msgEthnicity'],
          RACE_ETHNIC_CODE: ['hasErrorRaceEthnicCode', 'msgRaceEthnicCode'],
          RACE2_CODE: ['hasErrorRace2Code', 'msgRace2Code'],
          RACE3_CODE: ['hasErrorRace3Code', 'msgRace3Code'],
          RACE4_CODE: ['hasErrorRace4Code', 'msgRace4Code'],
          RACE5_CODE: ['hasErrorRace5Code', 'msgRace5Code'],
          ETHNIC_SUB_GROUP: ['hasErrorEthnicSubGroup', 'msgEthnicSubGroup'],
          STATE_STUDENT_ID: ['hasErrorStateId', 'msgStateId'],

          // Location Information Validation Errors
          SCHOOL_YEAR: ['hasErrorSchoolYear', 'msgSchoolYear'],
          DISTRICT_CODE: ['hasErrorDistrict', 'msgDistrict'],
          SCHOOL_CODE: ['hasErrorSchool', 'msgSchool'],
          RES_DISTRICT_CODE: ['hasErrorResDistrict', 'msgResDistrict'],
          LOCAL_STUDENT_ID: ['hasErrorLocalId', 'msgLocalId'],
          SOURCE_CODE: ['hasErrorSource', 'msgSource'],
          ALT_SOURCE_SIS_CODE: ['hasErrorAltSource', 'msgAltSource'],
          ALT_STUDENT_ID: ['hasErrorAltId', 'msgAltId'],
          ACTIVE: ['hasErrorStatus', 'msgStatus'],
          SOURCE_REFRESH_DATE: ['hasErrorRefreshDate', 'msgRefreshDate'],
          LOCATION_COMMENT: ['hasErrorLocComment', 'msgLocComment'],
          GRADE_CODE: ['hasErrorGrade', 'msgGrade'],

          // Contact Information Validation Errors
          STREET_1: ['hasErrorAddress1', 'msgAddress1'],
          STREET_2: ['hasErrorAddress2', 'msgAddress2'],
          CITY: ['hasErrorCity', 'msgEthnicity'],
          ZIP: ['hasErrorZip', 'msgZip'],
          GEOCODE: ['hasErrorGeoCode', 'msgGeoCode'],
          STATE_CODE: ['hasErrorState', 'msgState'],

          // Credential Information Vallidation Errors
          HIGHEST_DEGREE: ['hasErrorHighestDegree', 'msgHighestDegree'],
          SUBJECT_MATTER_AREA: ['hasErrorSubjectMatterArea', 'msgSubjectMatterArea'],
          DEGREE_PROVIDER: ['hasErrorDegreeProvider', 'msgDegreeProvider'],
          CERTIFICATE_NUMBER: ['hasErrorCertNumber', 'msgCertNumber'],
          CERTIFICATE_TYPE: ['hasErrorCertType', 'msgCertType'],
          // Other Information Vallidation Errors
          BIRTH_LOCATION: ['hasErrorBirthLocation', 'msgBirthLocation'],
          RESIDENCY_STATUS: ['hasErrorResidencyStatus', 'msgResidencyStatus'],
          RESIDENCY_EXPIRATION_DATE: ['hasErrorResidencyExpDate', 'msgResidencyExpDate'],
          MOTHERS_MAIDEN_NAME: ['hasErrorMothersMaidenName', 'msgMothersMaidenName'],

          // Customer Defined Fields Errors
          cdf_1: ['hasErrorCdf1', 'msgCdf1'],
          cdf_2: ['hasErrorCdf2', 'msgCdf2'],
          cdf_3: ['hasErrorCdf3', 'msgCdf3'],
          cdf_4: ['hasErrorCdf4', 'msgCdf4'],
          cdf_5: ['hasErrorCdf5', 'msgCdf5'],
          cdf_6: ['hasErrorCdf6', 'msgCdf6'],
          cdf_7: ['hasErrorCdf7', 'msgCdf7'],
          cdf_8: ['hasErrorCdf8', 'msgCdf8'],
          cdf_9: ['hasErrorCdf9Date', 'msgCdf9Date'],
          cdf_10: ['hasErrorCdf10Number', 'msgCdf10Number'],
          cdf_11: ['hasErrorCdf11Code', 'msgCdf11Code'],

          // Staff additional fields errors
          HIRE_DATE: ['hasErrorHireDate', 'msgHireDate'],
          SALARY: ['hasErrorSalary', 'msgSalary'],
          JOB_CLASS_CODE: ['hasErrorJobClass', 'msgJobClass'],
          JOB_ASSIGNMENT_1: ['hasErrorJobAssignment1', 'msgJobAssignment1'],
          JOB_ASSIGNMENT_2: ['hasErrorJobAssignment2', 'msgJobAssignment2'],
          JOB_ASSIGNMENT_3: ['hasErrorJobAssignment3', 'msgJobAssignment3'],
          ITINERANT_FLAG: ['hasErrorItinerantFlag', 'msgItinerantFlag'],
        };
        errors.forEach((e) => {
          // Person Information Validation Errors
          if (Object.keys(obj).includes(e.field)) {
            const [hasError, msg] = obj[e.field];
            this[hasError] = true;
            this[msg] = e.error.replace(e.field, `${this.fieldLabel(e.field)}`);
          }
          if (e.error.includes('User is not authorized to submit records to the specified district')) {
            const errorMessage = e.error.split(':')[0];
            this.hasErrorDistrict = true;
            this.msgDistrict = errorMessage.replace('district', this.fieldLabel('DISTRICT_CODE'));
          }
        });
      } else {
        this.resetNotifications();
      }
    },
    reset() {
      this.resetAllCommonEsSelects();
      if (this.isStudent) {
        this.resetEsSelectStudent();
      } else if (this.isEmployee) {
        this.resetStaff();
      }
      this.resetData();
    },
    resetAllCommonEsSelects() {
      if (this.$refs.resetAlternateSource) { this.$refs.resetAlternateSource.clear(); }
      if (this.$refs.resetCertType) { this.$refs.resetCertType.clear(); }
      if (this.$refs.resetDistrict) { this.$refs.resetDistrict.clear(); }
      if (this.$refs.resetEthnicSubgroup) { this.$refs.resetEthnicSubgroup.clear(); }
      if (this.$refs.resetEthnicity) { this.$refs.resetEthnicity.clear(); }
      if (this.$refs.resetHighestDegree) { this.$refs.resetHighestDegree.clear(); }
      if (this.$refs.resetRace) { this.$refs.resetRace.clear(); }
      if (this.$refs.resetRace2) { this.$refs.resetRace2.clear(); }
      if (this.$refs.resetRace3) { this.$refs.resetRace3.clear(); }
      if (this.$refs.resetRace4) { this.$refs.resetRace4.clear(); }
      if (this.$refs.resetRace5) { this.$refs.resetRace5.clear(); }
      if (this.$refs.resetResidencyStatus) { this.$refs.resetResidencyStatus.clear(); }
      if (this.$refs.resetResDistrict) { this.$refs.resetResDistrict.clear(); }
      if (this.$refs.resetSchool) { this.$refs.resetSchool.clear(); }
      if (this.allowedSchoolYear !== 'Current School Year Only') {
        this.$refs.resetSchoolYear.clear();
        this.$refs.resetSchoolYear.pushValues(this.schoolYear
          .filter(y => y.year === this.currentSchoolYear));
      }
      if (this.$refs.resetSource) { this.$refs.resetSource.clear(); }
      this.$refs.resetSource.pushValues(this.activeSources
        .filter(s => s.isDefault === true));
      if (this.$refs.resetState) { this.$refs.resetState.clear(); }
      if (this.$refs.resetStatus) {
        this.$refs.resetStatus.clear();
        this.$refs.resetStatus.pushValues(this.statusOptions
          .filter(el => el.code === 1));
      }
      if (this.$refs.resetSubjectMatterArea) { this.$refs.resetSubjectMatterArea.clear(); }
      if (this.$refs.resetCdf11) this.$refs.resetCdf11.clear();
      this.hasErrorAddress1 = false;
      this.hasErrorAddress2 = false;
      this.hasErrorAltId = false;
      this.hasErrorAltSource = false;
      this.hasErrorAltLastName = false;
      this.hasErrorBirthLocation = false;
      this.hasErrorCertNumber = false;
      this.hasErrorCertType = false;
      this.hasErrorCity = false;
      this.hasErrorCdf1 = false;
      this.hasErrorCdf2 = false;
      this.hasErrorCdf3 = false;
      this.hasErrorCdf4 = false;
      this.hasErrorCdf5 = false;
      this.hasErrorCdf6 = false;
      this.hasErrorCdf7 = false;
      this.hasErrorCdf8 = false;
      this.hasErrorCdf9Date = false;
      this.hasErrorCdf10Number = false;
      this.hasErrorCdf11Code = false;
      this.hasErrorDateOfBirth = false;
      this.hasErrorDegreeProvider = false;
      this.hasErrorDistrict = false;
      this.hasErrorEthnicity = false;
      this.hasErrorEthnicSubGroup = false;
      this.hasErrorFirstName = false;
      this.hasErrorGender = false;
      this.hasErrorGeoCode = false;
      this.hasErrorHighestDegree = false;
      this.hasErrorLastName = false;
      this.hasErrorLocalId = false;
      this.hasErrorLocComment = false;
      this.hasErrorMiddleName = false;
      this.hasErrorMothersMaidenName = false;
      this.hasErrorNickName = false;
      this.hasErrorFullName = false;
      this.hasErrorResDistrict = false;
      this.hasErrorResidencyStatus = false;
      this.hasErrorRaceEthnicCode = false;
      this.hasErrorRace2Code = false;
      this.hasErrorRace3Code = false;
      this.hasErrorRace4Code = false;
      this.hasErrorRace5Code = false;
      this.hasErrorresidencyExpDate = false;
      this.hasErrorRefreshDate = false;
      this.hasErrorState = false;
      this.hasErrorSubjectMatterArea = false;
      this.hasErrorSchool = false;
      this.hasErrorSchoolYear = false;
      this.hasErrorSource = false;
      this.hasErrorStatus = false;
      this.hasErrorSSN = false;
      this.hasErrorStateId = false;
      this.hasErrorSuffix = false;
      this.hasErrorZip = false;
      this.msgAddress1 = null;
      this.msgAddress2 = null;
      this.msgAltId = null;
      this.msgAltSource = null;
      this.msgAltLastName = null;
      this.msgBirthLocation = null;
      this.msgCertNumber = null;
      this.msgCertType = null;
      this.msgCity = null;
      this.msgCdf1 = null;
      this.msgCdf2 = null;
      this.msgCdf3 = null;
      this.msgCdf4 = null;
      this.msgCdf5 = null;
      this.msgCdf6 = null;
      this.msgCdf7 = null;
      this.msgCdf8 = null;
      this.msgCdf9Date = null;
      this.msgCdf10Number = null;
      this.msgCdf11Code = null;
      this.msgDateOfBirth = null;
      this.msgDegreeProvider = null;
      this.msgDistrict = null;
      this.msgEthnicity = null;
      this.msgEthnicSubGroup = null;
      this.msgFirstName = null;
      this.msgGender = null;
      this.msgGeoCode = null;
      this.msgHighestDegree = null;
      this.msgLastName = null;
      this.msgLocalId = null;
      this.msgLocComment = null;
      this.msgMiddleName = null;
      this.msgMothersMaidenName = null;
      this.msgNickName = null;
      this.msgFullName = null;
      this.msgResDistrict = null;
      this.msgResidencyStatus = null;
      this.msgRaceEthnicCode = null;
      this.msgRace2Code = null;
      this.msgRace3Code = null;
      this.msgRace4Code = null;
      this.msgRace5Code = null;
      this.msgResidencyExpDate = null;
      this.msgRefreshDate = null;
      this.msgSchool = null;
      this.msgSchoolYear = null;
      this.msgState = null;
      this.msgSubjectMatterArea = null;
      this.msgSource = null;
      this.msgStatus = null;
      this.msgStateId = null;
      this.msgSSN = null;
      this.msgSuffix = null;
      this.msgZip = null;
    },
    resetData() {
      const cacheAllSubmissionSpecs = this.allSubmissionSpecs;
      const cacheSubmissionPurposes = this.submissionPurposes;
      Object.assign(this.$data, this.initialData());
      this.allSubmissionSpecs = cacheAllSubmissionSpecs;
      this.setDataElements();
      this.resetNotifications();
      this.submissionPurposes = cacheSubmissionPurposes;
    },
    resetStaff() {
      if (this.$refs.resetJobClass) this.$refs.resetJobClass.clear();
      if (this.$refs.resetJobAssignment1) this.$refs.resetJobAssignment1.clear();
      if (this.$refs.resetJobAssignment2) this.$refs.resetJobAssignment2.clear();
      if (this.$refs.resetJobAssignment3) this.$refs.resetJobAssignment3.clear();
      if (this.$refs.resetItinerantFlag) this.$refs.resetItinerantFlag.clear();
      this.salary = null;
      this.salaryTemporary = null;
      this.hireDate = null;
      this.hasErrorSalary = false;
      this.hasErrorHireDate = false;
      this.hasErrorJobClass = false;
      this.hasErrorJobAssignment1 = false;
      this.hasErrorJobAssignment2 = false;
      this.hasErrorJobAssignment3 = false;
      this.hasErrorItinerantFlag = false;
      this.msgJobClass = null;
      this.msgJobAssignment1 = null;
      this.msgJobAssignment2 = null;
      this.msgJobAssignment3 = null;
      this.msgItinerantFlag = null;
      this.msgSalary = null;
      this.msgHireDate = null;
    },
    resetEsSelectStudent() {
      if (this.$refs.resetGrade) this.$refs.resetGrade.clear();
      this.hasErrorGrade = false;
      this.msgGrade = null;
    },
    resetNotifications() {
      this.hideNotification();
      this.$store.state.notifications.isActive = false;
      this.$store.state.notifications.notificationType = null;
    },
    setDataElements() {
      this.schoolYear = [this.schoolYears.find(a => a.year === this.currentSchoolYear)];
      this.userPersonTypesCodes = this.userPersonTypes
        .map(item => item.code);
      this.personType = this.userPersonTypesCodes.includes(this.defaultPersonType)
        ? this.defaultPersonType : this.userPersonTypesCodes[0];
      this.statusOptions = [
        {
          code: 0,
          description: 'Inactive',
        },
        {
          code: 1,
          description: 'Active',
        },
      ];
      this.statusOptions.filter(el => el.code === 1);
    },
    setSearchParams() {
      if (Object.keys(this.$route.params).length !== 0) {
        if (this.$route.params.searchParams.dateOfBirth) {
          this.dateOfBirth = this.$route.params.searchParams.dateOfBirth;
        }
        if (this.$route.params.searchParams.firstName) {
          this.firstName = this.$route.params.searchParams.firstName;
        }
        if (this.$route.params.searchParams.gender) {
          this.gender = this.$route.params.searchParams.gender;
        }
        if (this.$route.params.searchParams.lastName) {
          this.lastName = this.$route.params.searchParams.lastName;
        }
        if (this.$route.params.searchParams.idType === 'localId') {
          this.localStudentId = this.$route.params.searchParams.id;
        }
        this.middleName = this.$route.params.searchParams.middleName
          ? this.$route.params.searchParams.middleName : null;
        this.ssn = this.$route.params.searchParams.idType === 'ssn' ? this.$route.params.searchParams.id : null;
        this.stateId = this.$route.params.searchParams.idType === 'stateStudentId' ? this.$route.params.searchParams.id : null;
        this.suffix = this.$route.params.searchParams.suffix
          ? this.$route.params.searchParams.suffix : null;
        this.schoolCode = this.$route.params.searchParams.schoolCode
          ? this.$route.params.searchParams.schoolCode : null;
        this.districtCode = this.$route.params.districtCode
          ? this.$route.params.districtCode : null;
        this.personType = this.$route.params.searchParams.personType
          ? this.$route.params.searchParams.personType
          : (this.personType = this.userPersonTypesCodes.includes(this.defaultPersonType)
            ? this.defaultPersonType : this.userPersonTypesCodes[0]);
        if (this.$route.params.searchParams.sourceCode) {
          this.source = this.activeSources
            .filter(s => s.code === this.$route.params.searchParams.sourceCode);
        }
        if (this.$route.params.searchParams.active) {
          this.status = this.statusOptions
            .filter(s => s.code === this.$route.params.searchParams.active);
        }
      }
    },
    showError(error) {
      this.showNotification('danger', [error]);
    },
    showSuccess(error) {
      this.showNotification('success', [error]);
    },

    validateSelectField(field) {
      const ObjEsSelect = {
        // Person Information Validation
        ethnicity: ['ETHNICITY_INDICATOR', 'hasErrorEthnicity', 'msgEthnicity'],
        race1: ['RACE_ETHNIC_CODE', 'hasErrorRaceEthnicCode', 'msgRaceEthnicCode'],
        race2: ['RACE2_CODE', 'hasErrorRace2Code', 'msgRace2Code'],
        race3: ['RACE3_CODE', 'hasErrorRace3Code', 'msgRace3Code'],
        race4: ['RACE4_CODE', 'hasErrorRace4Code', 'msgRace4Code'],
        race5: ['RACE5_CODE', 'hasErrorRace5Code', 'msgRace5Code'],
        ethnicSubGroup: ['ETHNIC_SUB_GROUP', 'hasErrorEthnicSubGroup', 'msgEthnicSubGroup'],

        // Contact Information Validation
        state: ['STATE_CODE', 'hasErrorState', 'msgState'],

        // Credential Information Vallidation
        highestDegree: ['HIGHEST_DEGREE', 'hasErrorHighestDegree', 'msgHighestDegree'],
        subjectMatterArea: ['SUBJECT_MATTER_AREA', 'hasErrorSubjectMatterArea', 'msgSubjectMatterArea'],
        certType: ['CERTIFICATE_TYPE', 'hasErrorCertType', 'msgCertType'],

        // Other Information Validation
        residencyStatus: ['RESIDENCY_STATUS', 'hasErrorResidencyStatus', 'msgResidencyStatus'],

        // Location Information Vallidation
        schoolYear: ['SCHOOL_YEAR', 'hasErrorSchoolYear', 'msgSchoolYear'],
        district: ['DISTRICT_CODE', 'hasErrorDistrict', 'msgDistrict'],
        school: ['SCHOOL_CODE', 'hasErrorSchool', 'msgSchool'],
        resDistrict: ['RES_DISTRICT_CODE', 'hasErrorResDistrict', 'msgResDistrict'],
        source: ['SOURCE_CODE', 'hasErrorSource', 'msgSource'],
        altSource: ['ALT_SOURCE_SIS_CODE', 'hasErrorAltSource', 'msgAltSource'],
        status: ['ACTIVE', 'hasErrorStatus', 'msgStatus'],
        grade: ['GRADE_CODE', 'hasErrorGrade', 'msgGrade'],

        // Staff Validation
        jobClass: ['JOB_CLASS_CODE', 'hasErrorJobClass', 'msgJobClass'],
        jobAssignment1: ['JOB_ASSIGNMENT_1', 'hasErrorJobAssignment1', 'msgJobAssignment1'],
        jobAssignment2: ['JOB_ASSIGNMENT_2', 'hasErrorJobAssignment2', 'msgJobAssignment2'],
        jobAssignment3: ['JOB_ASSIGNMENT_3', 'hasErrorJobAssignment3', 'msgJobAssignment3'],
        itinerantFlag: ['ITINERANT_FLAG', 'hasErrorItinerantFlag', 'msgItinerantFlag'],

        cdf_11_code: ['cdf_11', 'hasErrorCdf11Code', 'msgCdf11Code'],
      };
      if (Object.keys(ObjEsSelect).includes(field)) {
        const [code, hasError, msg] = ObjEsSelect[field];
        const isFieldRequired = field === 'district' || field === 'school'
          || field === 'source' || field === 'schoolYear'
          ? this[field].length === 0 : this.fieldRequired(code) && this[field].length === 0;
        if (isFieldRequired) {
          this[hasError] = true;
          this[msg] = `${this.fieldLabel(`${code}`)} is required.`;
        } else {
          this[hasError] = false;
          this[msg] = null;
        }
      }
    },
    validateInputField(field) {
      const makeField = (code, hasErrorKey, msgKey) => [
        code, hasErrorKey, msgKey, this.fieldLength(code),
      ];

      const Obj = {
        // Person Information Validation
        firstName: makeField('FIRST_NAME', 'hasErrorFirstName', 'msgFirstName'),
        middleName: makeField('MIDDLE_NAME', 'hasErrorMiddleName', 'msgMiddleName'),
        lastName: makeField('LAST_NAME', 'hasErrorLastName', 'msgLastName'),
        suffix: makeField('SUFFIX', 'hasErrorSuffix', 'msgSuffix'),
        altLastName: makeField('PREVIOUS_LAST_NAME', 'hasErrorAltLastName', 'msgAltLastName'),
        nickName: makeField('AKA_NAME', 'hasErrorNickName', 'msgNickName'),
        fullName: makeField('FULL_NAME', 'hasErrorFullName', 'msgFullName'),
        stateId: makeField('STATE_STUDENT_ID', 'hasErrorStateId', 'msgStateId'),

        // Contact Information Validation
        address1: makeField('STREET_1', 'hasErrorAddress1', 'msgAddress1'),
        address2: makeField('STREET_2', 'hasErrorAddress2', 'msgAddress2'),
        city: makeField('CITY', 'hasErrorCity', 'msgCity'),
        geoCode: makeField('GEOCODE', 'hasErrorGeoCode', 'msgGeoCode'),

        // Credential Information Validation
        degreeProvider: makeField('DEGREE_PROVIDER', 'hasErrorDegreeProvider', 'msgDegreeProvider'),
        certNumber: makeField('CERTIFICATE_NUMBER', 'hasErrorCertNumber', 'msgCertNumber'),

        // Other Information Validation
        birthLocation: makeField('BIRTH_LOCATION', 'hasErrorBirthLocation', 'msgBirthLocation'),
        mothersMaidenName: makeField('MOTHERS_MAIDEN_NAME', 'hasErrorMothersMaidenName', 'msgMothersMaidenName'),

        // Customer Defined Fields Errors
        cdf_1: makeField('cdf_1', 'hasErrorCdf1', 'msgCdf1'),
        cdf_2: makeField('cdf_2', 'hasErrorCdf2', 'msgCdf2'),
        cdf_3: makeField('cdf_3', 'hasErrorCdf3', 'msgCdf3'),
        cdf_4: makeField('cdf_4', 'hasErrorCdf4', 'msgCdf4'),
        cdf_5: makeField('cdf_5', 'hasErrorCdf5', 'msgCdf5'),
        cdf_6: makeField('cdf_6', 'hasErrorCdf6', 'msgCdf6'),
        cdf_7: makeField('cdf_7', 'hasErrorCdf7', 'msgCdf7'),
        cdf_8: makeField('cdf_8', 'hasErrorCdf8', 'msgCdf8'),
        cdf_10_number: makeField('cdf_10', 'hasErrorCdf10Number', 'msgCdf10Number'),
        cdf_11_code: makeField('cdf_11', 'hasErrorCdf11Code', 'msgCdf11Code'),

        // Location Information Validation
        localStudentId: makeField('LOCAL_STUDENT_ID', 'hasErrorLocalId', 'msgLocalId'),
        altStudentId: makeField('ALT_STUDENT_ID', 'hasErrorAltId', 'msgAltId'),
        locComment: makeField('LOCATION_COMMENT', 'hasErrorLocComment', 'msgLocComment'),
      };

      if (Object.keys(Obj).includes(field)) {
        const [code, hasError, msg, maxLength] = Obj[field];
        if (this.fieldRequired(code) && !this[field]) {
          this[hasError] = true;
          this[msg] = `${this.fieldLabel(code)} is required.`;
        } else if (this[field] && this[field].length > maxLength) {
          this[hasError] = true;
          this[msg] = `${this.fieldLabel(code)} allows a maximum of ${maxLength} characters.`;
        } else {
          this[hasError] = false;
          this[msg] = null;
        }
      }
    },
    validateDateField(field) {
      const Obj = {

        residencyExpDate: ['RESIDENCY_EXPIRATION_DATE', 'hasErrorResidencyExpDate', 'msgResidencyExpDate'],
        refreshDate: ['SOURCE_REFRESH_DATE', 'hasErrorRefreshDate', 'msgRefreshDate'],
        hireDate: ['HIRE_DATE', 'hasErrorHireDate', 'msgHireDate'],
        cdf_9_date: ['cdf_9', 'hasErrorCdf9Date', 'msgCdf9Date'],
      };
      if (Object.keys(Obj).includes(field)) {
        const [code, hasError, msg] = Obj[field];
        if (this.fieldRequired(code) && !this[field]) {
          this[hasError] = true;
          this[msg] = `${this.fieldLabel(code)} is required.`;
        } else if (this[field] && !this.isValidDateFormat(this[field], 'YYYY-MM-DD')) {
          this[hasError] = true;
          this[msg] = `${this.fieldLabel(code)} is invalid.`;
        } else {
          this[hasError] = false;
          this[msg] = null;
        }
      }
    },
    validateField(field) {
      if (field === 'gender') {
        if (this.gender) {
          this.hasErrorGender = false;
          this.msgGender = null;
        } else {
          this.hasErrorGender = true;
          this.msgGender = `${this.fieldLabel('GENDER_CODE')
          } is required.`;
        }
      }
      if (field === 'dateOfBirth') {
        if (!this.dateOfBirth) {
          this.hasErrorDateOfBirth = true;
          this.msgDateOfBirth = `${this.fieldLabel('DATE_OF_BIRTH')
          } is required.`;
        } else if (this.dateOfBirth && !this.isValidDateFormat(this.dateOfBirth, 'YYYY-MM-DD')) {
          this.hasErrorDateOfBirth = true;
          this.msgDateOfBirth = `${this.fieldLabel('DATE_OF_BIRTH')
          } is invalid.`;
        } else if (this.dateOfBirth && !this.futureBirthYearsAllowed
          && !this.isValidDOB(this.dateOfBirth, 'YYYY-MM-DD')) {
          this.hasErrorDateOfBirth = true;
          this.msgDateOfBirth = `${this.fieldLabel('DATE_OF_BIRTH')
          } is invalid.`;
        } else if (this.dateOfBirth && this.futureBirthYearsAllowed
          && !this.isValidDOBWithfutureBirthYearsAllowed(this.dateOfBirth, 'YYYY-MM-DD')) {
          this.hasErrorDateOfBirth = true;
          this.msgDateOfBirth = `${this.fieldLabel('DATE_OF_BIRTH')
          } is invalid.`;
        } else {
          this.hasErrorDateOfBirth = false;
          this.msgDateOfBirth = '';
        }
      }
      if (field === 'ssn') {
        let ssnPattern;
        if (this.allowSinSsn) {
          // Allows:
          // - 9 digits: 123456789
          // - S followed by 8 digits: S12345678 or s12345678
          // - Dashed SSNs: 123-45-6789
          // - S followed by dashed: S12-34-5678 or s12-34-5678
          ssnPattern = /^(?:\d{9}|[Ss]\d{8}|\d{3}-\d{2}-\d{4}|[Ss]\d{2}-\d{2}-\d{4})$/;
        } else {
          // Only allows:
          // - 9 digits: 123456789
          // - Dashed SSNs: 123-45-6789
          // NO S/s prefix
          ssnPattern = /^(?:\d{9}|\d{3}-\d{2}-\d{4})$/;
        }
        const rawSSN = this.ssn;
        const ssn = rawSSN && rawSSN.trim() !== '' ? rawSSN.trim() : null;

        const isRequired = this.fieldRequired('SOCIAL_SECURITY_NUMBER');

        if (isRequired && !ssn) {
          this.hasErrorSSN = true;
          this.msgSSN = `${this.fieldLabel('SOCIAL_SECURITY_NUMBER')} is required.`;
        } else if (ssn && !ssnPattern.test(ssn)) {
          this.hasErrorSSN = true;
          this.msgSSN = `${this.fieldLabel('SOCIAL_SECURITY_NUMBER')} must be a valid format.`;
        } else {
          this.hasErrorSSN = false;
          this.msgSSN = null;
        }
      }
      if (field === 'salary') {
        if (this.fieldRequired('SALARY') && !this.salary) {
          this.hasErrorSalary = true;
          this.msgSalary = `${this.fieldLabel('SALARY')} is required.`;
        } else if (this.salary && this.salary.split('.')[0].length > this.fieldLength('SALARY')) {
          this.hasErrorSalary = true;
          this.msgSalary = `${this.fieldLabel('SALARY')} exceeds character limit 10.2`;
        } else {
          this.hasErrorSalary = false;
          this.msgSalary = null;
        }
      }
      if (field === 'zip') {
        if (this.fieldRequired('ZIP') && !this.zip) {
          this.hasErrorZip = true;
          this.msgZip = `${this.fieldLabel('ZIP')
          } is required.`;
        } else if (this.zip && this.zip.length > this.fieldLength('ZIP')) {
          this.hasErrorZip = true;
          this.msgZip = `${this.fieldLabel('ZIP')
          } must be of ${this.fieldLength('ZIP')} characters.`;
        } else if (!this.isValidZip(this.zip)) {
          this.hasErrorZip = true;
          this.msgZip = `${this.fieldLabel('ZIP')
          } is invalid.`;
        } else {
          this.hasErrorZip = false;
          this.msgZip = '';
        }
      }
    },
    validateSubmissionPurpose() {
      // validate state id has a value if submissionPurpose requires it
      if (!this.stateId && this.submissionPurposeRequiresStateId) {
        this.msgStateId = `${this.fieldLabel('STATE_STUDENT_ID')} is required.`;
        this.hasErrorStateId = true;
      }
    },
  },
};
</script>

<style lang="sass" scoped>
  @import "~bulma/sass/utilities/initial-variables.sass"
  @import "~bulma/sass/utilities/mixins.sass"

  @include from($desktop)
    .master-record--column
      border-right: 1px solid #dee2e6
      &:last-child
        border-right: none
  .label
    display: block
    min-width: 165px
  .gender-empty
    border-color: #ff3860 !important
  .input-group
    border-collapse: collapse
    width: 100%
  .input-group > div
    display: table-cell
    border: 1px solid #CED4DA
    vertical-align: middle
  .input-group-icon
    background: #eee
    color: #777
    padding: 0 10px
  .input-group input
    border: 0
    display: block
    width: 100%
    padding: 8px
  .header-subtitle
    text-align: left
    padding: 0 1.5rem 1.5rem 1.5rem
    font-size: 0.875rem
  .es-form-section
    .row.removeUnderline
      display: flex
      flex-wrap: wrap
  .columns
    display: flex
    flex-wrap: wrap
    .column.master-record--column
      flex: 1 1 auto
      .field.is-horizontal
        width: 100%
        flex-wrap: wrap
    .columns.is-multicolumns
      .column.master-record--column
        flex: 0 0 33.333%
  @media screen and (max-width: 70em)
    .columns.is-multicolumns .column.master-record--column
      flex: 0 1 100% !important
</style>
